import { formatCommentText, relativeTime as fmtRelativeTime } from './comment-format.js';

class FeedController {
  constructor() {
    this.exploreContainer = document.getElementById('explore-container');
    this.feedContainer = document.getElementById('feed-container');
    this.feedScroll = document.getElementById('feed-scroll');
    this.exploreGrid = document.getElementById('explore-grid');
    this.currentVideo = null;
    this.currentPostId = null;
    this.observer = null;
    this.progressBar = null;
    this.loadingDot = null;
    this.offset = 0;
    this.limit = 20;
    this.hasMore = true;
    this.isLoadingMore = false;
    this.currentTab = 'all';
    this.allPosts = [];
    this.isMobile = window.innerWidth < 768;
    this.feedOffset = 0;
    this.feedStartOffset = 0;
    this.feedHasMore = true;
    this.feedHasPrevious = false;
    this.isLoadingFeed = false;
    this.isLoadingPreviousFeed = false;
    this.feedScrollObserver = null;
    this.postFeedMode = this.feedContainer?.dataset?.postFeed === 'true';
    this.staticPostMode = this.feedContainer?.dataset?.staticPost === 'true';
    this.updateUrlOnScroll = this.feedContainer?.dataset?.updateUrl === 'true';
    this.initialPostId = this.feedContainer?.dataset?.initialPostId || null;
    this.galleryUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    this.galleryFeedHistoryActive = false;
    this.activeFeedItem = null;
    this.lastUrlPostId = null;
    this.feedNavUp = null;
    this.feedNavDown = null;
    this.baseQuery = '';
    this.gridQuery = '';
    this.gridOffsets = {};
    this.seenPostIds = new Set();
    this.seenMediaKeys = new Set();
    this.seenTextKeys = new Set();
    this.prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    this.isEbank = window.location.hostname.toLowerCase().includes('ebank');
    this.gridVideoObserver = null;
    this.shortcutDialog = null;
    this.init();
  }

  init() {
    this.createProgressIndicators();
    this.initSearchChain();
    if (this.staticPostMode) {
      this.setupStaticPost();
      return;
    }
    if (this.postFeedMode) {
      this.setupGridView();
      this.setupDialogs();
      this.setupShareDialog();
      this.setupCommentEmojiPicker();
      this.setupEmojiPicker();
      this.setupKeyboardShortcuts();
      this.setupFeedNav();
      if (this.initialPostId) {
        this.openFullscreenView(this.initialPostId);
      } else if (this.isMobile) {
        this.loadMobileFeed();
      }
      return;
    }
    this.setupGridView();
    this.setupTabs();
    this.setupDialogs();
    this.setupUpload();
    this.applyExternalComposerHandoff();
    this.setupShareDialog();
    this.setupEmojiPicker();
    this.setupCommentEmojiPicker();
    this.setupMentions();
    this.setupAIArt();
    this.setupGridInfiniteScroll();
    this.setupSearchAutocomplete();
    this.setupKeyboardShortcuts();
    this.setupFeedNav();
    this.setupGalleryHistory();
    this.countInitialPosts();
    this.selectedIndex = -1;

    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });

    if (this.isMobile) {
      this.loadMobileFeed();
    }
  }

  setupStaticPost() {
    this.setupDialogs();
    this.setupShareDialog();
    this.setupCommentEmojiPicker();
    this.setupEmojiPicker();
    (this.feedScroll || document).querySelectorAll('.feed-item').forEach(item => {
      this.currentPostId = item.dataset.postId;
      this.decorateFeedItemForKeyboard(item);
      item.querySelector('.like-btn')?.addEventListener('click', (e) => this.handleLike(e));
      item.querySelector('.follow-btn')?.addEventListener('click', (e) => this.handleFollow(e));
      item.querySelector('.comment-btn')?.addEventListener('click', (e) => this.handleCommentOpen(e));
      item.querySelector('.share-btn')?.addEventListener('click', (e) => this.handleShare(e));
      item.querySelector('.repost-btn')?.addEventListener('click', (e) => this.handleRepost(e));
      item.querySelector('.quote-btn')?.addEventListener('click', (e) => this.handleQuote(item.dataset.postId));
      item.querySelector('.edit-btn')?.addEventListener('click', (e) => this.handleEdit(e));
      item.querySelector('.delete-btn')?.addEventListener('click', (e) => this.handleDelete(e));
      const liked = this.isPostLiked(parseInt(item.dataset.postId));
      const likeBtn = item.querySelector('.like-btn');
      if (liked && likeBtn) { likeBtn.classList.add('liked'); likeBtn.setAttribute('aria-pressed', 'true'); }
    });
    document.querySelectorAll('.art-share-btn').forEach(btn => this.attachArtShareButton(btn));
    this.hydrateLinkPreviews(this.feedScroll || document);
    this.setupKeyboardShortcuts();
    this.setupStaticPostBackNav();
  }

  setupStaticPostBackNav() {
    document.querySelectorAll('.post-topbar a[href="/gallery"]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.tryHistoryBackToGallery()) return;
        window.location.href = link.getAttribute('href') || '/gallery';
      });
    });
  }

  tryHistoryBackToGallery() {
    try {
      if (window.history.length > 1 && document.referrer) {
        const ref = new URL(document.referrer, window.location.href);
        if (ref.origin === window.location.origin) {
          window.history.back();
          return true;
        }
      }
    } catch {}
    return false;
  }

  countInitialPosts() {
    const items = document.querySelectorAll('.grid-item[data-type="post"]');
    this.offset = items.length;
    items.forEach(item => {
      const postId = item.dataset.postId;
      if (postId) {
        this.allPosts.push({ id: parseInt(postId), element: item });
        this.seenPostIds.add(parseInt(postId));
      }
      const media = item.querySelector('.grid-thumb');
      const mediaUrl = media?.currentSrc || media?.src || media?.querySelector?.('source')?.src || '';
      const mediaKey = this.galleryMediaKey(mediaUrl);
      const textKey = this.galleryTextKey(media?.alt || '');
      if (mediaKey) this.seenMediaKeys.add(mediaKey);
      if (textKey) this.seenTextKeys.add(textKey);
    });
    const key = this.gridQuery || this.baseQuery || '';
    this.gridOffsets[key] = this.offset;
  }

  appendAllowNSFWParam(params) {
    if (window.addAllowNSFWParam) {
      window.addAllowNSFWParam(params);
      return;
    }
    const user = window.userData;
    if (user && (user.allow_nsfw || user.AllowNSFW)) {
      params.set('allow_nsfw', 'true');
    }
  }

  buildFeedUrl(offset, limit, query) {
    const params = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
    });
    const q = typeof query === 'string' ? query : '';
    if (q) {
      params.set('query', q);
    }
    this.appendAllowNSFWParam(params);
    return `/feed?${params.toString()}`;
  }

  buildAnchoredFeedUrl(anchorId, limit, query) {
    const params = new URLSearchParams({
      anchor_id: String(anchorId),
      limit: String(limit),
    });
    const q = typeof query === 'string' ? query : '';
    if (q) {
      params.set('query', q);
    }
    this.appendAllowNSFWParam(params);
    return `/feed?${params.toString()}`;
  }

  getPostsArray(data) {
    return Array.isArray(data?.posts) ? data.posts : [];
  }

  initSearchChain() {
    const params = new URLSearchParams(window.location.search || '');
    const rawQuery = (params.get('q') || params.get('tag') || params.get('query') || '').trim();
    this.baseQuery = rawQuery;
    this.gridQuery = rawQuery;
  }

  createProgressIndicators() {
    this.progressBar = document.createElement('div');
    this.progressBar.className = 'feed-progress-bar';
    document.body.appendChild(this.progressBar);

    this.loadingDot = document.createElement('div');
    this.loadingDot.className = 'feed-loading-dot';
    document.body.appendChild(this.loadingDot);
  }

  showLoading() {
    this.progressBar.classList.add('loading');
    this.progressBar.classList.remove('complete');
    this.loadingDot.classList.add('active');
  }

  hideLoading() {
    this.progressBar.classList.remove('loading');
    this.progressBar.classList.add('complete');
    this.loadingDot.classList.remove('active');
    setTimeout(() => {
      this.progressBar.classList.remove('complete');
      this.progressBar.style.width = '0';
    }, 500);
  }

  setupTabs() {
    document.querySelectorAll('.explore-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const activeTab = document.querySelector('.explore-tab.active');
        activeTab?.classList.remove('active');
        activeTab?.setAttribute('aria-selected', 'false');
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        this.currentTab = tab.dataset.tab;
        this.filterGrid();
      });
    });
  }

  filterGrid() {
    document.querySelectorAll('.grid-item').forEach(item => {
      const type = item.dataset.type;
      const isVideo = item.classList.contains('grid-video');

      if (this.currentTab === 'all') {
        item.style.display = '';
      } else if (this.currentTab === 'art' && type === 'post' && !isVideo) {
        item.style.display = '';
      } else if (this.currentTab === 'video' && isVideo) {
        item.style.display = '';
      } else if (this.currentTab === 'characters' && type === 'character') {
        item.style.display = '';
      } else {
        item.style.display = 'none';
      }
    });
  }

  setupGridView() {
    document.querySelectorAll('.grid-item').forEach(item => {
      this.attachGridItemListener(item);
    });

    document.getElementById('feed-back-btn')?.addEventListener('click', () => {
      if (this.postFeedMode) {
        const backHref = this.feedContainer?.dataset?.backHref;
        if (backHref) {
          window.location.href = backHref;
          return;
        }
        if (window.history.length > 1) {
          window.history.back();
          return;
        }
        window.location.href = '/gallery';
        return;
      }
      if (this.galleryFeedHistoryActive) {
        window.history.back();
        return;
      }
      this.switchToGridView();
    });

    document.getElementById('create-post-btn')?.addEventListener('click', (e) => {
      if (!this.isAuthenticated()) {
        if (typeof window.showSignupDialog === 'function') {
          window.showSignupDialog(e);
        } else {
          window.location.href = '/signup?next=/gallery';
        }
        return;
      }
      document.getElementById('create-post-dialog')?.showModal();
    });

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('compose') === '1' || window.location.hash === '#create') {
        if (this.isAuthenticated()) {
          setTimeout(() => { document.getElementById('create-post-dialog')?.showModal(); }, 50);
        } else {
          if (typeof window.showSignupDialog === 'function') {
            setTimeout(() => { window.showSignupDialog(null); }, 200);
          } else {
            window.location.href = '/signup?next=/gallery%3Fcompose%3D1';
          }
        }
      }
    } catch (e) {}

    document.getElementById('create-art-btn')?.addEventListener('click', () => {
      this.openAIArtGenerator();
    });

    document.querySelectorAll('.close-dialog').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.closest('dialog')?.close();
      });
    });

    document.querySelectorAll('dialog .mdl-button.close').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.closest('dialog')?.close();
      });
    });

    this.createRecommendationsOverlay();
    this.setupGridVideoObserver();
  }

  placeGridItem(item) {
    if (!this.exploreGrid) return;
    const sentinel = document.getElementById('grid-sentinel');
    if (sentinel && this.exploreGrid.contains(sentinel)) {
      this.exploreGrid.insertBefore(item, sentinel);
      return;
    }
    this.exploreGrid.appendChild(item);
  }

  attachGridItemListener(item) {
    const video = item.querySelector('.grid-thumb');
    if (video && video.tagName === 'VIDEO') {
      this.observeGridVideo(video);
      if (!this.prefersReducedMotion) {
        item.addEventListener('mouseenter', () => {
          video.play().catch(() => {});
        });
        item.addEventListener('mouseleave', () => {
          video.pause();
          video.currentTime = 0;
        });
      }
    }

    const type = item.dataset.type || 'post';
    item.setAttribute('tabindex', '0');
    item.setAttribute('role', type === 'character' ? 'link' : 'button');
    const characterName = item.querySelector('.character-name')?.textContent?.trim();
    const mediaAlt = item.querySelector('img.grid-thumb')?.getAttribute('alt')?.trim();
    const label = characterName || mediaAlt || (type === 'character' ? 'Character' : 'Post');
    const ariaLabel = type === 'character' ? `Chat with ${label}` : `Open ${label}`;
    item.setAttribute('aria-label', ariaLabel);
    item.querySelectorAll('.art-share-btn').forEach(btn => this.attachArtShareButton(btn));
    this.bindGridItemLongPress(item);

    item.addEventListener('click', (e) => {
      if (Date.now() < (this.gridSheetSuppressUntil || 0)) return;
      if (e.target.closest('.art-share-btn, .nie-launch, .grid-tag')) return;
      if (this.isMobile) {
        const isVisible = item.classList.contains('mobile-controls-visible');
        this.exploreGrid?.querySelectorAll('.mobile-controls-visible').forEach((visibleItem) => {
          if (visibleItem !== item) visibleItem.classList.remove('mobile-controls-visible');
        });
        if (!isVisible) {
          e.preventDefault();
          item.classList.add('mobile-controls-visible');
          return;
        }
      }
      const type = item.dataset.type;
      if (type === 'character') {
        const image = e.target.closest('.grid-thumb');
        if (image && !this.isOpaqueImagePixel(image, e.clientX, e.clientY)) {
          item.style.pointerEvents = 'none';
          const underneath = document.elementFromPoint(e.clientX, e.clientY);
          item.style.pointerEvents = '';
          if (underneath) underneath.dispatchEvent(new MouseEvent('click', e));
          return;
        }
        e.preventDefault();
        const name = item.dataset.name || item.dataset.slug;
        if (name) {
          window.location.href = `/ai-chat/${name}`;
        }
      } else {
        e.preventDefault();
        const postId = item.dataset.postId;
        this.openFullscreenView(postId);
      }
    });

    item.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        item.click();
      }
    });
  }

  bindGridItemLongPress(item) {
    let pressStart = null;
    let pressTimer = null;
    const clearPress = () => {
      pressStart = null;
      if (pressTimer) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };
    item.addEventListener(
      'touchstart',
      (event) => {
        if (event.target.closest('.art-share-btn, .nie-launch, .grid-tag')) return;
        if (!event.touches || event.touches.length !== 1) return;
        clearPress();
        const touch = event.touches[0];
        pressStart = { x: touch.clientX, y: touch.clientY };
        pressTimer = setTimeout(() => {
          pressTimer = null;
          if (!pressStart) return;
          clearPress();
          if (typeof navigator.vibrate === 'function') {
            try { navigator.vibrate(15); } catch {}
          }
          this.gridSheetSuppressUntil = Date.now() + 700;
          this.openGridItemSheet(item);
        }, 450);
      },
      { passive: true }
    );
    item.addEventListener(
      'touchmove',
      (event) => {
        if (!pressStart || !event.touches || !event.touches.length) return;
        const touch = event.touches[0];
        if (
          Math.abs(touch.clientX - pressStart.x) > 8 ||
          Math.abs(touch.clientY - pressStart.y) > 8
        ) {
          clearPress();
        }
      },
      { passive: true }
    );
    item.addEventListener('touchend', clearPress);
    item.addEventListener('touchcancel', clearPress);
    item.addEventListener('contextmenu', (event) => {
      if ((item.dataset.type || 'post') !== 'post' || !item.dataset.postId) return;
      event.preventDefault();
      this.openGridItemSheet(item);
    });
  }

  getGridItemSheetDialog() {
    let dlg = document.getElementById('grid-item-sheet');
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.id = 'grid-item-sheet';
      dlg.className = 'grid-item-sheet';
      dlg.innerHTML =
        '<div class="grid-item-sheet-title"></div>' +
        '<div class="grid-item-sheet-actions"></div>';
      document.body.appendChild(dlg);
      dlg.addEventListener('click', (e) => {
        if (e.target === dlg) dlg.close();
      });
    }
    return dlg;
  }

  getGridItemMediaURL(item) {
    const thumb = item.querySelector('img.grid-thumb, video.grid-thumb');
    if (!thumb) return '';
    if (thumb.tagName === 'VIDEO') {
      return thumb.currentSrc || thumb.querySelector('source')?.src || '';
    }
    return thumb.getAttribute('src') || thumb.currentSrc || '';
  }

  getGridItemPath(item) {
    const postId = item.dataset.postId;
    if (!postId) return '';
    const shareBtn = item.querySelector('.art-share-btn[data-share-url]');
    if (shareBtn) return shareBtn.getAttribute('data-share-url');
    const isVideo = !!item.querySelector('video.grid-thumb');
    return `${this.isEbank && !isVideo ? '/art/' : '/p/'}${postId}`;
  }

  copyGridText(value, button) {
    const done = () => {
      if (!button) return;
      const previous = button.textContent;
      button.textContent = 'Copied';
      setTimeout(() => { button.textContent = previous; }, 1200);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(done, () => {});
      return;
    }
    const input = document.createElement('textarea');
    input.value = value;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    try { document.execCommand('copy'); done(); } catch {}
    input.remove();
  }

  downloadGridMedia(url) {
    if (!url) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '';
    anchor.rel = 'noopener';
    anchor.target = '_blank';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  openGridItemSheet(item) {
    const postId = item.dataset.postId;
    if (!postId) return;
    const path = this.getGridItemPath(item);
    const url = new URL(path, window.location.origin).href;
    const mediaURL = this.getGridItemMediaURL(item);
    const title = (
      item.querySelector('img.grid-thumb')?.getAttribute('alt') || 'Post'
    ).trim().slice(0, 80);

    const dlg = this.getGridItemSheetDialog();
    dlg.querySelector('.grid-item-sheet-title').textContent = title || 'Post';
    const actions = dlg.querySelector('.grid-item-sheet-actions');
    actions.innerHTML = '';
    const addButton = (icon, label, onClick) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.innerHTML = `<i class="material-icons">${icon}</i>${label}`;
      btn.addEventListener('click', () => {
        dlg.close();
        onClick(btn);
      });
      actions.appendChild(btn);
    };

    addButton('open_in_new', 'Open post', () => this.openFullscreenView(postId));
    if (mediaURL) {
      addButton('download', 'Download media', () => this.downloadGridMedia(mediaURL));
    }
    addButton('link', 'Copy link', (btn) => this.copyGridText(url, btn));
    if (navigator.share) {
      addButton('share', 'Share', () =>
        navigator.share({ title, url }).catch(() => {})
      );
    } else {
      const shareBtn = item.querySelector('.art-share-btn');
      if (shareBtn) {
        addButton('share', 'Share', () => shareBtn.click());
      }
    }
    const editBtn = item.querySelector('.nie-launch');
    if (editBtn) {
      addButton('tune', 'Edit image', () => editBtn.click());
    }
    if (!dlg.open) dlg.showModal();
  }

  isOpaqueImagePixel(image, clientX, clientY) {
    if (!image.complete || !image.naturalWidth || !image.naturalHeight) return true;
    const rect = image.getBoundingClientRect();
    const scale = Math.max(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
    const renderedWidth = image.naturalWidth * scale;
    const renderedHeight = image.naturalHeight * scale;
    const x = Math.floor((clientX - rect.left - (rect.width - renderedWidth) / 2) / scale);
    const y = Math.floor((clientY - rect.top - (rect.height - renderedHeight) / 2) / scale);
    if (x < 0 || y < 0 || x >= image.naturalWidth || y >= image.naturalHeight) return false;
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    try {
      context.drawImage(image, 0, 0);
      return context.getImageData(x, y, 1, 1).data[3] > 0;
    } catch {
      return true;
    }
  }

  async openFullscreenView(postId, options = {}) {
    this.showLoading();
    this.feedScroll.innerHTML = '';
    this.feedOffset = 0;
    this.feedStartOffset = 0;
    this.feedHasMore = true;
    this.feedHasPrevious = false;
    this.isLoadingFeed = false;
    this.isLoadingPreviousFeed = false;

    try {
      const res = await fetch(this.buildAnchoredFeedUrl(postId, 50, this.baseQuery));
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();

      const rawPosts = this.getPostsArray(data);
      const targetId = parseInt(postId);
      let orderedPosts = rawPosts;
      if (postId && !orderedPosts.some(p => p.id === targetId)) {
        try {
          const postRes = await fetch(`/p/${postId}`, { headers: { Accept: 'application/json' } });
          if (postRes.ok) {
            const postData = await postRes.json();
            if (postData && postData.id) {
              orderedPosts = [postData, ...orderedPosts];
            }
          }
        } catch {}
      }

      this.feedStartOffset = Number.isFinite(data.offset) ? data.offset : 0;
      orderedPosts.forEach((post, index) => {
        const item = this.createFeedItem(post);
        this.feedScroll.appendChild(item);
        if ((index + 1) % 10 === 0) {
          const ad = this.createAdItem();
          this.feedScroll.appendChild(ad);
        }
      });

      this.feedOffset = this.feedStartOffset + rawPosts.length;
      this.feedHasMore = rawPosts.length > 0 && data.has_more !== false;
      this.feedHasPrevious = this.feedStartOffset > 0;

      this.setupFeedView();
      this.setupFeedInfiniteScroll();
      this.switchToFeedView();
      this.setGalleryFeedHistory(postId, options.pushHistory !== false);
      this.hideLoading();
      const targetItem = this.feedScroll.querySelector(`.feed-item[data-post-id="${postId}"]`) || this.feedScroll.querySelector('.feed-item[data-post-id]');
      if (targetItem) {
        requestAnimationFrame(() => {
          targetItem.scrollIntoView({ block: 'start' });
          this.setActiveFeedItem(targetItem);
        });
      }
    } catch (err) {
      this.hideLoading();
      console.error('Failed to open fullscreen:', err);
    }
  }

  async loadMobileFeed() {
    this.showLoading();
    this.feedScroll.innerHTML = '';
    this.feedOffset = 0;
    this.feedStartOffset = 0;
    this.feedHasMore = true;
    this.feedHasPrevious = false;
    this.isLoadingFeed = false;
    this.isLoadingPreviousFeed = false;

    try {
      const res = await fetch(this.buildFeedUrl(0, 50, this.baseQuery));
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();

      const posts = this.getPostsArray(data);
      this.feedStartOffset = Number.isFinite(data.offset) ? data.offset : 0;
      posts.forEach((post, index) => {
        const item = this.createFeedItem(post);
        this.feedScroll.appendChild(item);
        if ((index + 1) % 10 === 0) {
          const ad = this.createAdItem();
          this.feedScroll.appendChild(ad);
        }
      });

      this.feedOffset = this.feedStartOffset + posts.length;
      this.feedHasMore = posts.length > 0 && data.has_more !== false;
      this.feedHasPrevious = this.feedStartOffset > 0;

      this.setupFeedView();
      this.setupFeedInfiniteScroll();
      this.switchToFeedView();
      this.hideLoading();
    } catch (err) {
      this.hideLoading();
      console.error('Failed to load mobile feed:', err);
    }
  }

  switchToFeedView() {
    this.exploreContainer?.classList.add('hidden');
    this.feedContainer?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  switchToGridView() {
    this.feedContainer.classList.remove('active');
    this.exploreContainer.classList.remove('hidden');
    document.body.style.overflow = '';
    this.galleryFeedHistoryActive = false;
    this.activeFeedItem = null;
    this.lastUrlPostId = null;

    this.feedScroll.querySelectorAll('video').forEach(v => v.pause());
  }

  setupGalleryHistory() {
    if (this.postFeedMode) return;
    window.addEventListener('popstate', (e) => {
      if (e.state?.gallery_feed && e.state?.feed_post_id) {
        this.openFullscreenView(e.state.feed_post_id, { pushHistory: false });
        return;
      }
      if (this.feedContainer?.classList.contains('active')) {
        this.switchToGridView();
      }
    });
  }

  setGalleryFeedHistory(postId, pushHistory = true) {
    if (this.postFeedMode || !postId) return;
    this.galleryFeedHistoryActive = true;
    this.lastUrlPostId = String(postId);
    if (!pushHistory) return;
    try {
      window.history.pushState({ gallery_feed: true, feed_post_id: String(postId), gallery_url: this.galleryUrl }, '', `/p/${postId}`);
    } catch {}
  }

  setupFeedView() {
    this.observer = new window.IntersectionObserver((entries) => {
      let topEntry = null;
      entries.forEach(entry => {
        const item = entry.target;
        const video = item.querySelector('.feed-video');
        if (entry.isIntersecting) {
          if (video) this.playVideo(video, item);
          if (item.dataset.postId && (!topEntry || entry.intersectionRatio > topEntry.intersectionRatio)) {
            topEntry = entry;
          }
        } else {
          if (video) this.pauseVideo(video, item);
        }
      });
      if (topEntry) {
        this.setActiveFeedItem(topEntry.target);
      }
    }, { root: this.feedScroll, threshold: 0.6 });

    this.feedScroll.querySelectorAll('.feed-item').forEach(item => {
      this.observer.observe(item);
      this.attachFeedItemListeners(item);
    });
  }

  createFeedItem(post) {
    const item = document.createElement('div');
    item.className = 'feed-item';
    item.dataset.postId = post.id;
    const rawCaption = post.text || '';
    const safeCaption = this.escapeHtml(rawCaption);
    const captionHtml = this.linkifyPostText(rawCaption);
    const mediaLabel = safeCaption;
    const imageAlt = mediaLabel || 'Post image';
    const videoLabel = mediaLabel ? `Video: ${mediaLabel}` : 'Post video';

    let contentHTML = '';
    if (post.chat_replay) {
      contentHTML = this.createChatReplayHTML(post.chat_replay, post.id);
    } else if (post.game_data) {
      contentHTML = this.createGameShareHTML(post.game_data);
    } else if (post.media_url) {
      const isVideo = post.media_type === 'video';
      contentHTML = isVideo
        ? `<video class="feed-video" loop muted playsinline autoplay preload="metadata" aria-label="${videoLabel}">
             <source src="${post.media_url}" type="video/mp4">
           </video>`
        : `<img class="feed-image" src="${post.media_url}" alt="${imageAlt}" loading="lazy" decoding="async">`;
    }

    let quotedHTML = '';
    if (post.quoted_post) {
      quotedHTML = this.createQuotedPostHTML(post.quoted_post);
    }

    let linksHTML = '';
    if (post.links?.length && !post.media_url) {
      linksHTML = this.createLinkPreviewsHTML(post.links);
    }

    const tagsHTML = post.tags?.length
      ? `<div class="feed-tags">${post.tags.map(t => `<a class="feed-tag" href="/gallery?tag=${encodeURIComponent(t)}" rel="tag">#${this.escapeHtml(t)}</a>`).join('')}</div>`
      : '';

    const aiMetaHTML = '';
    const imgToolsHTML = post.media_url && post.media_type !== 'video' && !post.chat_replay && !post.game_data
      ? this.imageToolButtonsHTML(post.media_url)
      : '';
    const remixBtnHTML = post.media_url && !post.chat_replay && !post.game_data
      ? `<a class="feed-action remix-btn" href="/tools/video-editor?${post.media_type === 'video' ? 'videoUrl' : 'imageUrl'}=${encodeURIComponent(post.media_url)}&source=gallery&inline=1" data-inline-editor="true" data-editor-media="${encodeURIComponent(post.media_url)}" data-editor-type="${post.media_type === 'video' ? 'video' : 'image'}" title="Remix in video editor" aria-label="Remix in video editor">
            <i class="material-icons">${post.media_type === 'video' ? 'movie_edit' : 'movie'}</i>
          </a>`
      : '';

    const isLiked = this.isPostLiked(post.id);
    const authorName = this.escapeHtml(post.author_username || 'anonymous');
    const ownerHTML = this.isOwnPost(post) ? `
          <button class="feed-action edit-btn" data-post-id="${post.id}" title="Edit" aria-label="Edit post">
            <i class="material-icons">edit</i>
          </button>
          <button class="feed-action delete-btn" data-post-id="${post.id}" title="Delete" aria-label="Delete post">
            <i class="material-icons">delete</i>
          </button>` : '';
    item.innerHTML = `
      <div class="feed-media">${contentHTML}</div>
      <div class="feed-overlay">
        <div class="feed-actions">
          <button class="feed-action like-btn${isLiked ? ' liked' : ''}" data-post-id="${post.id}" aria-label="Like post" aria-pressed="${isLiked ? 'true' : 'false'}">
            <i class="material-icons">favorite</i>
            <span class="like-count">${post.likes || 0}</span>
          </button>
          <button class="feed-action comment-btn" data-post-id="${post.id}" aria-label="View comments">
            <i class="material-icons">chat_bubble</i>
            <span class="comment-count">${post.comment_count || 0}</span>
          </button>
          <button class="feed-action share-btn" data-post-id="${post.id}" aria-label="Share post">
            <i class="material-icons">share</i>
          </button>
          ${remixBtnHTML}
          ${imgToolsHTML}
          <button class="feed-action quote-btn" data-post-id="${post.id}" title="Quote" aria-label="Quote post">
            <i class="material-icons">format_quote</i>
          </button>
          <button class="feed-action repost-btn" data-post-id="${post.id}" aria-label="Repost">
            <i class="material-icons">repeat</i>
          </button>
          ${ownerHTML}
        </div>
        <div class="feed-info">
          <div class="feed-author">
            <img class="author-avatar" src="${post.author_avatar || '/static/img/android-chrome-192x192.png'}" alt="@${authorName}" loading="lazy" decoding="async">
            <span class="author-name">@${authorName}</span>
            <button class="follow-btn" aria-pressed="false" aria-label="Follow @${authorName}">Follow</button>
          </div>
          <p class="feed-caption">${captionHtml}</p>
          ${aiMetaHTML}
          ${linksHTML}
          ${quotedHTML}
          ${tagsHTML}
        </div>
      </div>`;

    this.hydrateLinkPreviews(item);
    this.decorateFeedItemForKeyboard(item);
    if (post.chat_replay) {
      setTimeout(() => this.setupChatReplay(item, post.chat_replay), 100);
    }

    return item;
  }

  decorateFeedItemForKeyboard(item) {
    if (!item) return;
    item.tabIndex = -1;
    item.setAttribute('role', 'article');
    item.setAttribute('aria-label', `Post ${item.dataset.postId || ''}`.trim());
    const shortcuts = [
      ['.like-btn', 'L'],
      ['.comment-btn', 'C'],
      ['.share-btn', 'S'],
      ['.quote-btn', 'Q'],
      ['.repost-btn', 'R'],
    ];
    shortcuts.forEach(([selector, key]) => {
      item.querySelector(selector)?.setAttribute('aria-keyshortcuts', key);
    });
  }

  createAdItem() {
    const item = document.createElement('div');
    item.className = 'feed-item feed-ad';
    item.innerHTML = `
      <div class="feed-media">
        <div class="feed-ad-content">
          <div class="feed-ad-label">Sponsored</div>
          <div class="feed-ad-slot" id="feed-ad-${Date.now()}">
            <div class="feed-ad-placeholder">
              <i class="material-icons">campaign</i>
              <span>Advertisement</span>
            </div>
          </div>
          <a href="/advertise" class="feed-ad-cta">Advertise with us</a>
        </div>
      </div>`;
    return item;
  }

  createChatReplayHTML(chat, postId) {
    const charLink = chat.character_name ? `/ai-chat/${chat.character_name}` : '#';
    return `
      <div class="chat-replay" data-post-id="${postId}">
        <div class="chat-replay-header">
          <a href="${charLink}" class="chat-replay-avatar-link">
            <img class="chat-replay-avatar" src="${chat.char_avatar || '/static/img/android-chrome-192x192.png'}" alt="${chat.character_name || 'Character'}" loading="lazy" decoding="async">
          </a>
          <div class="chat-replay-info">
            <a href="${charLink}" class="chat-replay-title-link"><div class="chat-replay-title">${this.escapeHtml(chat.character_name || 'AI Character')}</div></a>
            <div class="chat-replay-subtitle">${chat.messages?.length || 0} messages</div>
          </div>
          <div class="chat-replay-controls">
            <button class="chat-replay-btn" data-action="play" title="Play" aria-label="Play chat replay"><i class="material-icons">play_arrow</i></button>
            <button class="chat-replay-btn" data-action="chat" title="Chat Now" aria-label="Chat now"><i class="material-icons">chat</i></button>
          </div>
        </div>
        <div class="chat-replay-messages"></div>
      </div>`;
  }

  createGameShareHTML(game) {
    const icons = { '2048': 'grid_4x4', 'default': 'sports_esports' };
    const icon = icons[game.game_type] || icons.default;
    return `
      <div class="game-share">
        <div class="game-share-icon"><i class="material-icons">${icon}</i></div>
        <div class="game-share-info">
          <div class="game-share-title">${this.escapeHtml(game.title || game.game_type)}</div>
          <div class="game-share-score">Score: ${game.score?.toLocaleString() || 0}</div>
        </div>
        <button class="game-play-btn">Play Now</button>
      </div>`;
  }

  createQuotedPostHTML(quoted) {
    if (!quoted) return '';
    const hasMedia = quoted.media_url && quoted.media_type === 'image';
    const authorName = this.escapeHtml(quoted.author_username || 'anonymous');
    return `
      <div class="quoted-post" data-post-id="${quoted.id}">
        <div class="quoted-header">
          <img class="quoted-avatar" src="${quoted.author_avatar || '/static/img/android-chrome-192x192.png'}" alt="@${authorName}" loading="lazy" decoding="async">
          <span class="quoted-author">@${authorName}</span>
        </div>
        ${hasMedia ? `<img class="quoted-image" src="${quoted.media_url}" alt="Quoted image" loading="lazy" decoding="async">` : ''}
        <p class="quoted-text">${this.linkifyPostText(quoted.text || '')}</p>
      </div>`;
  }

  trimPostURL(raw) {
    let value = String(raw || '').replace(/[.,!?;:]+$/g, '');
    [['(', ')'], ['[', ']'], ['{', '}']].forEach(([open, close]) => {
      while (value.endsWith(close) && value.split(close).length > value.split(open).length) {
        value = value.slice(0, -1);
      }
    });
    return value;
  }

  findPostLinks(text) {
    const matches = [];
    const pattern = /https?:\/\/[^\s<>"']+/gi;
    let match;
    while ((match = pattern.exec(String(text || ''))) !== null) {
      const url = this.trimPostURL(match[0]);
      if (url) matches.push({ index: match.index, end: match.index + url.length, url });
    }
    return matches;
  }

  firstPostURL(text) {
    return this.findPostLinks(text)[0]?.url || '';
  }

  displayPostURL(url) {
    let display = String(url || '').replace(/^https?:\/\//i, '');
    try {
      const parsed = new URL(url);
      if (!parsed.pathname || parsed.pathname === '/') {
        display = display.replace(/\/$/, '');
      }
    } catch {}
    return display;
  }

  linkifyPostText(text) {
    const value = String(text || '');
    const matches = this.findPostLinks(value);
    if (!matches.length) return this.escapeHtml(value).replace(/\n/g, '<br>');
    let output = '';
    let cursor = 0;
    matches.forEach(link => {
      output += this.escapeHtml(value.slice(cursor, link.index));
      output += `<a class="post-link" href="${this.escapeHtml(link.url)}" target="_blank" rel="noopener">${this.escapeHtml(this.displayPostURL(link.url))}</a>`;
      cursor = link.end;
    });
    output += this.escapeHtml(value.slice(cursor));
    return output.replace(/\n/g, '<br>');
  }

  createLinkPreviewsHTML(links) {
    if (!links?.length) return '';
    const link = links[0];
    const domain = this.extractDomain(link);
    return `
      <a href="${this.escapeHtml(link)}" target="_blank" rel="noopener" class="link-preview" data-url="${this.escapeHtml(link)}">
        <div class="link-preview-icon"><i class="material-icons">link</i></div>
        <div class="link-preview-info">
          <div class="link-preview-title">${this.escapeHtml(domain)}</div>
          <div class="link-preview-url">${this.escapeHtml(this.displayPostURL(link))}</div>
        </div>
        <i class="material-icons link-preview-arrow">open_in_new</i>
      </a>`;
  }

  hydrateLinkPreviews(root) {
    root?.querySelectorAll?.('.link-preview[data-url]').forEach(link => {
      if (link.dataset.unfurlState) return;
      link.dataset.unfurlState = 'loading';
      const url = link.dataset.url;
      fetch(`/api/link-preview?url=${encodeURIComponent(url)}`)
        .then(response => response.ok ? response.json() : null)
        .then(data => {
          if (!data) return;
          const info = link.querySelector('.link-preview-info');
          const title = link.querySelector('.link-preview-title');
          if (title && data.title) title.textContent = data.title;
          if (info && data.description) {
            let description = info.querySelector('.link-preview-desc');
            if (!description) {
              description = document.createElement('div');
              description.className = 'link-preview-desc';
              info.insertBefore(description, info.querySelector('.link-preview-url'));
            }
            description.textContent = String(data.description).slice(0, 160);
          }
          if (data.image && !link.querySelector('.link-preview-thumb')) {
            const image = document.createElement('img');
            image.className = 'link-preview-thumb';
            image.alt = '';
            image.loading = 'lazy';
            try {
              image.src = new URL(data.image, url).href;
              link.querySelector('.link-preview-icon')?.replaceWith(image);
            } catch {}
          }
          link.dataset.unfurlState = 'ready';
        })
        .catch(() => {
          link.dataset.unfurlState = 'fallback';
        });
    });
  }

  extractDomain(url) {
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace('www.', '');
    } catch {
      return url;
    }
  }

  setupChatReplay(item, chat) {
    const replay = item.querySelector('.chat-replay');
    if (!replay) return;

    const messagesEl = replay.querySelector('.chat-replay-messages');
    const playBtn = replay.querySelector('[data-action="play"]');
    const chatBtn = replay.querySelector('[data-action="chat"]');
    const messages = chat.messages || [];
    let playing = false;
    let currentIdx = 0;
    let timeoutId = null;

    const showMessage = (msg, withAnim = true) => {
      const isAI = msg.role === 'assistant';
      const avatar = isAI ? chat.char_avatar : '/static/img/android-chrome-192x192.png';
      const div = document.createElement('div');
      div.className = `chat-msg ${isAI ? 'ai' : 'user'}`;
      if (withAnim) div.style.opacity = '0';
      div.innerHTML = `
        <img class="chat-msg-avatar" src="${avatar || '/static/img/android-chrome-192x192.png'}" alt="">
        <div class="chat-msg-bubble">${this.escapeHtml(msg.content || '')}</div>`;
      messagesEl.appendChild(div);
      if (withAnim) {
        requestAnimationFrame(() => {
          div.style.transition = 'opacity 0.3s, transform 0.3s';
          div.style.opacity = '1';
        });
      }
      messagesEl.scrollTop = messagesEl.scrollHeight;
    };

    const showTyping = () => {
      const div = document.createElement('div');
      div.className = 'chat-msg ai typing-indicator';
      div.innerHTML = `
        <img class="chat-msg-avatar" src="${chat.char_avatar || '/static/img/android-chrome-192x192.png'}" alt="">
        <div class="chat-msg-bubble chat-msg-typing"><span></span><span></span><span></span></div>`;
      messagesEl.appendChild(div);
      messagesEl.scrollTop = messagesEl.scrollHeight;
    };

    const hideTyping = () => {
      const t = messagesEl.querySelector('.typing-indicator');
      if (t) t.remove();
    };

    const playNext = () => {
      if (!playing || currentIdx >= messages.length) {
        playing = false;
        if (playBtn) playBtn.querySelector('i').textContent = 'replay';
        return;
      }
      const msg = messages[currentIdx];
      if (msg.role === 'assistant') {
        showTyping();
        timeoutId = setTimeout(() => {
          hideTyping();
          showMessage(msg);
          currentIdx++;
          timeoutId = setTimeout(playNext, 800);
        }, 1000 + Math.random() * 500);
      } else {
        showMessage(msg);
        currentIdx++;
        timeoutId = setTimeout(playNext, 600);
      }
    };

    playBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (playing) {
        playing = false;
        clearTimeout(timeoutId);
        playBtn.querySelector('i').textContent = 'play_arrow';
      } else {
        if (currentIdx >= messages.length) {
          messagesEl.innerHTML = '';
          currentIdx = 0;
        }
        playing = true;
        playBtn.querySelector('i').textContent = 'pause';
        playNext();
      }
    });

    chatBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (chat.character_name) {
        window.location.href = `/ai-chat/${chat.character_name}`;
      }
    });

    if (messages.length <= 3) {
      messages.forEach(msg => showMessage(msg, false));
      currentIdx = messages.length;
    }
  }

  attachFeedItemListeners(item) {
    const video = item.querySelector('.feed-video');
    let lastTap = 0;

    item.addEventListener('click', (e) => {
      if (e.target.closest('.feed-action') || e.target.closest('.feed-info') || e.target.closest('.follow-btn')) return;
      const now = Date.now();
      if (now - lastTap < 300) {
        this.doubleTapLike(item);
        lastTap = 0;
      } else {
        lastTap = now;
        if (video) {
          if (video.paused) this.playVideo(video, item);
          else this.pauseVideo(video, item);
        }
      }
    });

    this.setupSwipeGesture(item);

    item.querySelector('.like-btn')?.addEventListener('click', (e) => this.handleLike(e));
    item.querySelector('.follow-btn')?.addEventListener('click', (e) => this.handleFollow(e));
    item.querySelector('.comment-btn')?.addEventListener('click', (e) => this.handleCommentOpen(e));
    item.querySelector('.share-btn')?.addEventListener('click', (e) => this.handleShare(e));
    item.querySelector('.repost-btn')?.addEventListener('click', (e) => this.handleRepost(e));
    item.querySelector('.edit-btn')?.addEventListener('click', (e) => this.handleEdit(e));
    item.querySelector('.delete-btn')?.addEventListener('click', (e) => this.handleDelete(e));
  }

  isOwnPost(post) {
    const author = String(post.author_username || '').trim().toLowerCase();
    if (!author) return false;
    const u = window.userData || {};
    const uname = String(u.username || '').trim().toLowerCase();
    const email = String(u.email || '').trim().toLowerCase();
    if (post.author_user_id && u.id && String(post.author_user_id) === String(u.id)) return true;
    return author === uname || (!!email && author === email);
  }

  async handleEdit(e) {
    const btn = e.currentTarget;
    const postId = btn.dataset.postId;
    const item = btn.closest('.feed-item');
    const caption = item?.querySelector('.feed-caption');
    const current = caption ? caption.textContent.trim() : '';
    const updated = window.prompt('Edit post', current);
    if (updated == null || updated.trim() === '' || updated.trim() === current) return;
    try {
      const res = await fetch('/api/update-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: postId, text: updated.trim() })
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (caption) caption.innerHTML = this.escapeHtml(data.text || updated.trim()).replace(/\n/g, '<br>');
      this.showToast('Post updated');
    } catch {
      this.showToast('Edit failed');
    }
  }

  async handleDelete(e) {
    const btn = e.currentTarget;
    const postId = btn.dataset.postId;
    if (!window.confirm('Delete this post?')) return;
    try {
      const res = await fetch('/api/delete-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: postId })
      });
      if (!res.ok) throw new Error('failed');
      const item = btn.closest('.feed-item');
      item?.remove();
      this.showToast('Post deleted');
    } catch {
      this.showToast('Delete failed');
    }
  }

  playVideo(video, item) {
    if (this.currentVideo && this.currentVideo !== video) {
      this.currentVideo.pause();
    }
    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => {});
    });
    item.classList.remove('paused');
    this.currentVideo = video;
  }

  pauseVideo(video, item) {
    video.pause();
    item.classList.add('paused');
  }

  doubleTapLike(item) {
    const postId = item.dataset.postId;
    const likeBtn = item.querySelector('.like-btn');

    let heart = item.querySelector('.double-tap-heart');
    if (!heart) {
      heart = document.createElement('i');
      heart.className = 'material-icons double-tap-heart';
      heart.textContent = 'favorite';
      item.querySelector('.feed-media').appendChild(heart);
    }

    heart.classList.remove('animate');
    void heart.offsetWidth;
    heart.classList.add('animate');

    if (likeBtn && !this.isPostLiked(postId)) {
      likeBtn.click();
    }
  }

  handleFollow(e) {
    if (!this.isAuthenticated()) {
      if (typeof window.showSignupDialog === 'function') {
        window.showSignupDialog(e);
      } else {
        window.location.href = '/signup?next=/gallery';
      }
      return;
    }
    const btn = e.currentTarget;
    btn.classList.toggle('following');
    const isFollowing = btn.classList.contains('following');
    btn.textContent = isFollowing ? 'Following' : 'Follow';
    btn.setAttribute('aria-pressed', isFollowing ? 'true' : 'false');
  }

  isPostLiked(postId) {
    try {
      const liked = JSON.parse(localStorage.getItem('liked_posts') || '[]');
      return liked.includes(String(postId));
    } catch { return false; }
  }

  setPostLiked(postId) {
    try {
      const liked = JSON.parse(localStorage.getItem('liked_posts') || '[]');
      if (!liked.includes(String(postId))) {
        liked.push(String(postId));
        localStorage.setItem('liked_posts', JSON.stringify(liked));
      }
    } catch {}
  }

  async handleLike(e) {
    const btn = e.currentTarget;
    const postId = btn.dataset.postId;
    const countEl = btn.querySelector('.like-count');

    if (this.isPostLiked(postId)) {
      return;
    }

    btn.classList.add('liked');
    btn.setAttribute('aria-pressed', 'true');
    this.setPostLiked(postId);

    try {
      const formData = new FormData();
      formData.append('id', postId);

      const res = await fetch('/api/like-post', {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        countEl.textContent = data.likes;
      }
    } catch (err) {
      console.error('Like failed:', err);
    }
  }

  async handleCommentOpen(e) {
    const btn = e.currentTarget;
    this.currentPostId = btn.dataset.postId;

    const dialog = document.getElementById('comments-dialog');
    const list = document.getElementById('comments-list');

    list.innerHTML = '<div class="no-comments"><span>Loading...</span></div>';
    dialog?.showModal();
    if (!this.isMobile) document.getElementById('comment-input')?.focus();
    this.showLoading();

    try {
      const res = await fetch(`/api/comments?post_id=${this.currentPostId}`, { cache: 'no-store' });
      this.hideLoading();
      if (res.ok) {
        const comments = await res.json();
        this.renderComments(comments);
      } else {
        list.innerHTML = this.emptyCommentsHtml();
      }
    } catch {
      this.hideLoading();
      list.innerHTML = this.emptyCommentsHtml();
    }
  }

  renderComments(comments) {
    const list = document.getElementById('comments-list');
    if (!comments || comments.length === 0) {
      list.innerHTML = this.emptyCommentsHtml();
      return;
    }

    list.innerHTML = comments.map(c => {
      const name = this.escapeHtml(c.author_username || 'anonymous');
      const time = this.relativeTime(c.created || c.created_at);
      const timeHtml = time ? `<span class="comment-time">${time}</span>` : '';
      return `
      <div class="comment-item" role="listitem" data-comment-id="${c.id}">
        <img class="comment-avatar" src="${c.author_avatar || '/static/img/android-chrome-192x192.png'}" alt="@${name}" loading="lazy" decoding="async">
        <div class="comment-content">
          <div class="comment-author">@${name}${timeHtml}</div>
          <div class="comment-text">${this.renderCommentText(c.text)}</div>
          <div class="comment-reactions" data-comment-id="${c.id}">
            <button class="reaction-add" data-comment-id="${c.id}" aria-label="Add reaction" title="Add reaction"><i class="material-icons">add_reaction</i></button>
          </div>
        </div>
      </div>`;
    }).join('');
    this.loadCommentReactions();
  }

  async loadCommentReactions() {
    if (!this.currentPostId) return;
    try {
      const res = await fetch(`/api/comment-reactions?post_id=${this.currentPostId}`, { credentials: 'include', cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const reactions = data?.data?.reactions || data?.reactions || [];
      this.renderReactionChips(reactions);
    } catch {}
  }

  renderReactionChips(reactions) {
    document.querySelectorAll('#comments-list .comment-reactions').forEach(container => {
      container.querySelectorAll('.reaction-chip').forEach(el => el.remove());
      const commentId = parseInt(container.dataset.commentId, 10);
      const addBtn = container.querySelector('.reaction-add');
      reactions.filter(r => r.comment_id === commentId).forEach(r => {
        const chip = document.createElement('button');
        chip.className = 'reaction-chip' + (r.mine ? ' mine' : '');
        chip.dataset.commentId = commentId;
        if (r.custom_emoji_id) {
          chip.dataset.customEmojiId = r.custom_emoji_id;
          chip.title = r.emoji_name ? `:${r.emoji_name}: — click to react and add to your emoji` : 'Click to react and add to your emoji';
          const img = document.createElement('img');
          img.src = r.image_url;
          img.alt = r.emoji_name || 'custom emoji';
          img.className = 'reaction-chip-img';
          chip.appendChild(img);
        } else {
          chip.dataset.emoji = r.emoji;
          chip.appendChild(document.createTextNode(r.emoji));
        }
        const count = document.createElement('span');
        count.className = 'reaction-count';
        count.textContent = r.count;
        chip.appendChild(count);
        container.insertBefore(chip, addBtn);
      });
    });
  }

  async toggleCommentReaction(commentId, { emoji = '', customEmojiId = 0 } = {}) {
    try {
      const res = await fetch('/api/comment-reactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          post_id: parseInt(this.currentPostId, 10),
          comment_id: parseInt(commentId, 10),
          emoji,
          custom_emoji_id: customEmojiId ? parseInt(customEmojiId, 10) : 0,
        }),
      });
      if (res.status === 401 || res.status === 403) {
        this.showToast('Log in to react');
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      const payload = data?.data || data;
      if (payload?.grabbed && payload?.emoji?.name) {
        this.customEmojiCache = null;
        this.showToast(`:${payload.emoji.name}: added to your emoji`);
      }
      this.loadCommentReactions();
    } catch {}
  }

  async fetchCustomEmojis() {
    if (this.customEmojiCache) return this.customEmojiCache;
    try {
      const res = await fetch('/api/custom-emoji/list', { credentials: 'include', cache: 'no-store' });
      if (!res.ok) return [];
      const data = await res.json();
      this.customEmojiCache = data?.data?.emojis || data?.emojis || [];
      return this.customEmojiCache;
    } catch {
      return [];
    }
  }

  async uploadCustomEmoji(file) {
    const name = (file.name || 'emoji').replace(/\.[a-z0-9]+$/i, '');
    const fd = new FormData();
    fd.append('name', name);
    fd.append('image', file);
    const res = await fetch('/api/custom-emoji/upload', { method: 'POST', body: fd, credentials: 'include' });
    if (res.status === 401 || res.status === 403) {
      this.showToast('Log in to upload emoji');
      return null;
    }
    if (!res.ok) {
      let msg = 'Upload failed';
      try { msg = (await res.json())?.error || msg; } catch {}
      this.showToast(msg);
      return null;
    }
    const data = await res.json();
    this.customEmojiCache = null;
    return data?.data || data;
  }

  openReactionPicker(commentId) {
    this.reactionCommentId = commentId;
    const mdlDialog = document.getElementById('emoji-modal-dialog');
    const mdlList = document.getElementById('emoji-list');
    if (!mdlDialog || !mdlList) return;
    mdlDialog.showModal();
    this.renderReactionPicker(mdlList);
    const mdlSearch = document.getElementById('emoji-search');
    if (mdlSearch) {
      mdlSearch.value = '';
      if (!this.isMobile) mdlSearch.focus();
    }
  }

  async renderReactionPicker(container) {
    const popular = ['👍', '❤️', '😂', '🔥', '🎉', '😮', '😢', '💯', '👀', '🙏', '✨', '🚀', '😍', '🤔', '👏', '😈', '💀', '🤖', '🦄', '🌸', '🥳', '😎', '🫶', '⭐️'];
    container.innerHTML = '<div class="custom-emoji-section"><div class="custom-emoji-title">Your emoji <button type="button" class="custom-emoji-upload-btn" id="reaction-upload-emoji">+ Upload</button></div><div class="custom-emoji-grid" id="reaction-custom-grid"><span class="custom-emoji-loading">Loading…</span></div></div>'
      + popular.map(e => `<button class="emoji-btn" data-emoji="${e}">${e}</button>`).join('');

    container.querySelectorAll('.emoji-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.pickReaction({ emoji: btn.dataset.emoji });
      });
    });

    container.querySelector('#reaction-upload-emoji')?.addEventListener('click', () => {
      let input = document.getElementById('reaction-emoji-file');
      if (!input) {
        input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png,image/jpeg,image/gif,image/webp';
        input.id = 'reaction-emoji-file';
        input.hidden = true;
        document.body.appendChild(input);
      }
      input.onchange = async () => {
        const file = input.files && input.files[0];
        input.value = '';
        if (!file) return;
        const uploaded = await this.uploadCustomEmoji(file);
        if (uploaded) {
          this.showToast(`:${uploaded.name}: uploaded`);
          this.renderCustomEmojiGrid(document.getElementById('reaction-custom-grid'));
        }
      };
      input.click();
    });

    this.renderCustomEmojiGrid(container.querySelector('#reaction-custom-grid'));
  }

  async renderCustomEmojiGrid(grid) {
    if (!grid) return;
    const emojis = await this.fetchCustomEmojis();
    grid.innerHTML = '';
    if (!emojis.length) {
      grid.innerHTML = '<span class="custom-emoji-empty">No custom emoji yet — upload your own, or tap one on a comment to grab it</span>';
      return;
    }
    emojis.forEach(e => {
      const btn = document.createElement('button');
      btn.className = 'emoji-btn custom';
      btn.title = `:${e.name}:`;
      const img = document.createElement('img');
      img.src = e.image_url;
      img.alt = e.name;
      btn.appendChild(img);
      btn.addEventListener('click', () => this.pickReaction({ customEmojiId: e.id }));
      grid.appendChild(btn);
    });
  }

  pickReaction({ emoji = '', customEmojiId = 0 }) {
    const mdlDialog = document.getElementById('emoji-modal-dialog');
    if (mdlDialog?.open) mdlDialog.close();
    if (this.reactionCommentId) {
      this.toggleCommentReaction(this.reactionCommentId, { emoji, customEmojiId });
      this.reactionCommentId = null;
    }
  }

  emptyCommentsHtml() {
    return '<div class="no-comments"><i class="material-icons">mode_comment</i><span>No comments yet</span><small>Be the first to comment</small></div>';
  }

  handleShare(e) {
    const postId = e.currentTarget.dataset.postId;
    this.currentSharePostId = postId;
    this.currentShareUrl = `${window.location.origin}${this.isEbank ? '/art/' : '/p/'}${postId}`;
    const feedItem = e.currentTarget.closest('.feed-item');
    const imageEl = feedItem?.querySelector('.feed-image');
    const videoSource = feedItem?.querySelector('.feed-video source');
    this.currentShareMediaUrl = imageEl?.src || videoSource?.src || '';
    const caption = feedItem?.querySelector('.feed-caption')?.textContent?.trim();
    this.currentShareText = caption || 'Netwrck Gallery';

    this.openShareDialog();
  }

  attachArtShareButton(btn) {
    if (!btn || btn.dataset.shareBound === 'true') return;
    btn.dataset.shareBound = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleArtShare(btn);
    });
  }

  handleArtShare(btn) {
    const postId = btn.dataset.postId;
    const path = btn.dataset.shareUrl || `${this.isEbank ? '/art/' : '/p/'}${postId}`;
    this.currentSharePostId = postId;
    this.currentShareUrl = new URL(path, window.location.origin).href;
    this.currentShareMediaUrl = btn.dataset.shareMedia || btn.closest('.grid-item, .art-related-card')?.querySelector('img, video')?.src || '';
    this.currentShareText = btn.dataset.shareText || btn.closest('.grid-item, .art-related-card')?.querySelector('img')?.alt || (this.isEbank ? 'AI Art Gallery' : 'Netwrck Gallery');
    this.openShareDialog();
  }

  openShareDialog() {
    const shareDialog = document.getElementById('share-dialog');
    const shareNative = document.getElementById('share-native');
    if (shareNative) {
      shareNative.style.display = typeof navigator.share === 'function' ? 'flex' : 'none';
    }
    const shareCopyImage = document.getElementById('share-copy-image');
    if (shareCopyImage) {
      shareCopyImage.style.display = this.currentShareMediaUrl ? 'flex' : 'none';
    }
    shareDialog?.showModal();
  }

  setupShareDialog() {
    const getShareText = (includeMedia) => {
      const base = this.currentShareText || (this.isEbank ? 'AI Art Gallery' : 'Netwrck Gallery');
      if (includeMedia && this.currentShareMediaUrl) {
        return `${base} ${this.currentShareMediaUrl}`.trim();
      }
      return base;
    };
    const closeShareDialog = () => document.getElementById('share-dialog')?.close();

    document.getElementById('share-copy')?.addEventListener('click', async () => {
      await navigator.clipboard?.writeText(this.currentShareUrl);
      this.showToast('Link copied!');
      closeShareDialog();
    });

    document.getElementById('share-copy-image')?.addEventListener('click', async () => {
      const targetUrl = this.currentShareMediaUrl || this.currentShareUrl;
      if (!targetUrl) return;
      await navigator.clipboard?.writeText(targetUrl);
      this.showToast(this.currentShareMediaUrl ? 'Image link copied!' : 'Link copied!');
      closeShareDialog();
    });

    document.getElementById('share-twitter')?.addEventListener('click', () => {
      const url = encodeURIComponent(this.currentShareUrl);
      const text = encodeURIComponent(getShareText(true));
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
      closeShareDialog();
    });

    document.getElementById('share-facebook')?.addEventListener('click', () => {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.currentShareUrl)}`, '_blank');
      closeShareDialog();
    });

    document.getElementById('share-reddit')?.addEventListener('click', () => {
      const url = encodeURIComponent(this.currentShareUrl);
      const title = encodeURIComponent(getShareText(false));
      window.open(`https://www.reddit.com/submit?url=${url}&title=${title}`, '_blank');
      closeShareDialog();
    });

    document.getElementById('share-linkedin')?.addEventListener('click', () => {
      const url = encodeURIComponent(this.currentShareUrl);
      const title = encodeURIComponent(getShareText(false));
      window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`, '_blank');
      closeShareDialog();
    });

    document.getElementById('share-native')?.addEventListener('click', async () => {
      try {
        const text = getShareText(true);
        await navigator.share({ title: this.currentShareText || (this.isEbank ? 'AI Art Generator' : 'Netwrck'), text, url: this.currentShareUrl });
      } catch {}
      closeShareDialog();
    });
  }

  setupEmojiPicker() {
    const btn = document.getElementById('add-emoji-btn');
    const textEl = document.getElementById('post-text');

    const mdlDialog = document.getElementById('emoji-modal-dialog');
    if (mdlDialog) {
      this.emojiDialog = mdlDialog;
      btn?.addEventListener('click', () => {
        mdlDialog.showModal();
        if (typeof componentHandler !== 'undefined') {
          componentHandler.upgradeDom();
        }
        const mdlSearch = document.getElementById('emoji-search');
        const mdlList = document.getElementById('emoji-list');
        if (mdlList && !mdlList.innerHTML) {
          this.loadPopularEmojis(mdlList, textEl);
        }
        mdlSearch?.focus();
      });

      const closeBtn = mdlDialog.querySelector('.close');
      closeBtn?.addEventListener('click', () => mdlDialog.close());

      mdlDialog.addEventListener('click', (e) => {
        if (e.target === mdlDialog) mdlDialog.close();
      });

      const mdlSearch = document.getElementById('emoji-search');
      const mdlList = document.getElementById('emoji-list');
      let debounceTimer;
      mdlSearch?.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const query = mdlSearch.value.trim();
        if (query.length < 2) {
          this.loadPopularEmojis(mdlList, textEl);
          return;
        }
        debounceTimer = setTimeout(() => this.searchEmojis(query, mdlList, textEl), 200);
      });
      return;
    }

    const picker = document.getElementById('emoji-picker');
    const searchInput = document.getElementById('emoji-search-input');
    const results = document.getElementById('emoji-results');

    if (!btn || !picker) return;

    btn.addEventListener('click', () => {
      picker.classList.toggle('active');
      if (picker.classList.contains('active')) {
        searchInput?.focus();
        if (!results.innerHTML) this.loadPopularEmojis(results, textEl);
      }
    });

    let debounceTimer;
    searchInput?.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const query = searchInput.value.trim();
      if (query.length < 2) {
        this.loadPopularEmojis(results, textEl);
        return;
      }
      debounceTimer = setTimeout(() => this.searchEmojis(query, results, textEl), 200);
    });

    document.addEventListener('click', (e) => {
      if (!picker.contains(e.target) && e.target !== btn && !btn.contains(e.target)) {
        picker.classList.remove('active');
      }
    });
  }

  setupCommentEmojiPicker() {
    const btn = document.getElementById('comment-emoji-btn');
    const input = document.getElementById('comment-input');
    const mdlDialog = document.getElementById('emoji-modal-dialog');
    if (!btn || !input || !mdlDialog) return;

    this.commentEmojiTarget = input;

    btn.addEventListener('click', () => {
      this.commentEmojiTarget = input;
      mdlDialog.showModal();
      if (typeof componentHandler !== 'undefined') {
        componentHandler.upgradeDom();
      }
      const mdlSearch = document.getElementById('emoji-search');
      const mdlList = document.getElementById('emoji-list');
      if (mdlList) {
        this.loadPopularEmojisForInput(mdlList, input);
      }
      mdlSearch?.focus();
    });
  }

  loadPopularEmojisForInput(container, inputEl) {
    const popular = ['😀', '😁', '😂', '🤣', '🥰', '😍', '😘', '😊', '😉', '😇', '😌', '😋', '😜', '🤓', '😎', '🤩', '🥳', '🤗', '🤔', '😅', '🥲', '😭', '🥹', '🥺', '😴', '🤯', '😤', '😈', '👻', '💀', '👽', '🤖', '💯', '🔥', '✨', '🌟', '⭐️', '⚡️', '🌈', '☀️', '🌙', '🌌', '🌊', '🌸', '🌻', '🍀', '🌿', '🎉', '🎨', '🎮', '🎧', '🎬', '🎯', '🏆', '🥇', '🎁', '🚀', '🧠', '🧩', '🧸', '🐶', '🐱', '🦄', '🦊', '🐼', '🐸', '🐙', '🦋', '🍕', '🍔', '🍣', '🍩', '🧋', '🍓', '🍉', '🍇', '🍿', '🍰', '🍫', '🥂', '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤', '💕', '👍', '🙏', '🙌', '👏', '✌️', '🤝', '✅', '❗️', '👀', '💪', '🫶'];
    container.innerHTML = popular.map(e => `<button class="emoji-btn">${e}</button>`).join('');
    container.querySelectorAll('.emoji-btn').forEach(btn => {
      btn.addEventListener('click', () => this.insertEmojiToInput(btn.textContent, inputEl));
    });
  }

  insertEmojiToInput(emoji, inputEl) {
    if (this.reactionCommentId) {
      this.pickReaction({ emoji });
      return;
    }
    if (!inputEl) return;
    if (this.emojiDialog) this.emojiDialog.close();
    const mdlDialog = document.getElementById('emoji-modal-dialog');
    if (mdlDialog?.open) mdlDialog.close();

    const start = inputEl.selectionStart || inputEl.value.length;
    const end = inputEl.selectionEnd || inputEl.value.length;
    const text = inputEl.value;
    inputEl.value = text.slice(0, start) + emoji + text.slice(end);
    inputEl.focus();
    inputEl.setSelectionRange(start + emoji.length, start + emoji.length);
  }

  loadPopularEmojis(container, textEl) {
    const popular = ['😀', '😁', '😂', '🤣', '🥰', '😍', '😘', '😊', '😉', '😇', '😌', '😋', '😜', '🤓', '😎', '🤩', '🥳', '🤗', '🤔', '😅', '🥲', '😭', '🥹', '🥺', '😴', '🤯', '😤', '😈', '👻', '💀', '👽', '🤖', '💯', '🔥', '✨', '🌟', '⭐️', '⚡️', '🌈', '☀️', '🌙', '🌌', '🌊', '🌸', '🌻', '🍀', '🌿', '🎉', '🎨', '🎮', '🎧', '🎬', '🎯', '🏆', '🥇', '🎁', '🚀', '🧠', '🧩', '🧸', '🐶', '🐱', '🦄', '🦊', '🐼', '🐸', '🐙', '🦋', '🍕', '🍔', '🍣', '🍩', '🧋', '🍓', '🍉', '🍇', '🍿', '🍰', '🍫', '🥂', '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤', '💕', '👍', '🙏', '🙌', '👏', '✌️', '🤝', '✅', '❗️', '👀', '💪', '🫶'];
    container.innerHTML = popular.map(e => `<button class="emoji-btn">${e}</button>`).join('');
    container.querySelectorAll('.emoji-btn').forEach(btn => {
      btn.addEventListener('click', () => this.insertEmoji(btn.textContent, textEl));
    });
  }

  async searchEmojis(query, container, textEl) {
    try {
      const res = await fetch(`/api/emoji-search?q=${encodeURIComponent(query)}&limit=24`);
      if (res.ok) {
        const data = await res.json();
        const emojis = data.results || [];
        if (emojis.length === 0) {
          container.innerHTML = '<p class="no-results">No emojis found</p>';
          return;
        }
        container.innerHTML = emojis.map(e => `<button class="emoji-btn" title="${e.category}">${e.emoji}</button>`).join('');
        container.querySelectorAll('.emoji-btn').forEach(btn => {
          btn.addEventListener('click', () => this.insertEmoji(btn.textContent, textEl));
        });
      }
    } catch {
      container.innerHTML = '<p class="no-results">Search failed</p>';
    }
  }

  insertEmoji(emoji, textEl) {
    if (this.reactionCommentId) {
      this.pickReaction({ emoji });
      return;
    }
    if (!textEl) return;
    if (this.emojiDialog) this.emojiDialog.close();
    textEl.focus();
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();

    if (!textEl.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(textEl);
      range.collapse(false);
    }

    const textNode = document.createTextNode(emoji);
    range.deleteContents();
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  setupMentions() {
    const textEl = document.getElementById('post-text');
    if (!textEl) return;

    this.mentionPopup = document.createElement('div');
    this.mentionPopup.id = 'mention-popup';
    this.mentionPopup.className = 'mention-popup';
    this.mentionPopup.innerHTML = '<div class="mention-results"></div>';
    document.body.appendChild(this.mentionPopup);

    this.mentionQuery = '';
    this.mentionRange = null;

    textEl.addEventListener('input', () => this.checkForMention(textEl));
    textEl.addEventListener('keydown', (e) => this.handleMentionKeydown(e));

    document.addEventListener('click', (e) => {
      if (!this.mentionPopup.contains(e.target)) {
        this.hideMentionPopup();
      }
    });
  }

  checkForMention(textEl) {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const text = textEl.innerText;
    const cursorPos = this.getCaretPosition(textEl);

    const beforeCursor = text.substring(0, cursorPos);
    const match = beforeCursor.match(/@(\w*)$/);

    if (match) {
      this.mentionQuery = match[1];
      this.mentionRange = range.cloneRange();
      this.showMentionPopup(textEl);
      if (this.mentionQuery.length >= 1) {
        this.searchMentions(this.mentionQuery);
      } else {
        this.loadPopularMentions();
      }
    } else {
      this.hideMentionPopup();
    }
  }

  getCaretPosition(el) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return 0;
    const range = sel.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(el);
    preRange.setEnd(range.endContainer, range.endOffset);
    return preRange.toString().length;
  }

  showMentionPopup(textEl) {
    const rect = textEl.getBoundingClientRect();
    const sel = window.getSelection();
    if (!sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    const caretRect = range.getBoundingClientRect();

    this.mentionPopup.style.left = `${caretRect.left}px`;
    this.mentionPopup.style.top = `${caretRect.bottom + 5}px`;
    this.mentionPopup.classList.add('active');
  }

  hideMentionPopup() {
    this.mentionPopup.classList.remove('active');
    this.mentionQuery = '';
  }

  async searchMentions(query) {
    const results = this.mentionPopup.querySelector('.mention-results');
    try {
      const res = await fetch(`/api/mention-search?q=${encodeURIComponent(query)}&limit=6`);
      if (res.ok) {
        const chars = await res.json();
        if (chars.length === 0) {
          results.innerHTML = '<div class="mention-empty">No characters found</div>';
        } else {
          this.renderMentionResults(chars, results);
        }
      }
    } catch {
      results.innerHTML = '<div class="mention-empty">Search failed</div>';
    }
  }

  async loadPopularMentions() {
    const results = this.mentionPopup.querySelector('.mention-results');
    try {
      const res = await fetch('/api/mention-search?q=&limit=6');
      if (res.ok) {
        const chars = await res.json();
        this.renderMentionResults(chars, results);
      }
    } catch {
      results.innerHTML = '<div class="mention-empty">Loading...</div>';
    }
  }

  renderMentionResults(chars, container) {
    container.innerHTML = chars.map(c => `
      <button class="mention-item" data-slug="${c.slug}" data-name="${c.name}">
        <img class="mention-avatar" src="${c.avatar || '/static/img/android-chrome-192x192.png'}" alt="">
        <div class="mention-info">
          <span class="mention-name">${this.escapeHtml(c.name)}</span>
          <span class="mention-handle">@${c.slug}</span>
        </div>
        ${c.type ? `<span class="mention-type">${c.type}</span>` : ''}
      </button>
    `).join('');

    container.querySelectorAll('.mention-item').forEach(item => {
      item.addEventListener('click', () => {
        this.insertMention(item.dataset.name, item.dataset.slug);
      });
    });
  }

  insertMention(name, slug) {
    const textEl = document.getElementById('post-text');
    if (!textEl) return;

    const text = textEl.innerText;
    const cursorPos = this.getCaretPosition(textEl);
    const beforeCursor = text.substring(0, cursorPos);
    const afterCursor = text.substring(cursorPos);

    const atPos = beforeCursor.lastIndexOf('@');
    if (atPos === -1) return;

    const newBefore = beforeCursor.substring(0, atPos);
    const mentionText = `@${name} `;
    const newText = newBefore + mentionText + afterCursor;

    textEl.innerHTML = '';
    const beforeNode = document.createTextNode(newBefore);
    const mentionNode = document.createElement('span');
    mentionNode.className = 'mention-tag';
    mentionNode.dataset.slug = slug;
    mentionNode.contentEditable = 'false';
    mentionNode.textContent = `@${name}`;
    const afterNode = document.createTextNode(' ' + afterCursor);

    textEl.appendChild(beforeNode);
    textEl.appendChild(mentionNode);
    textEl.appendChild(afterNode);

    const range = document.createRange();
    const sel = window.getSelection();
    range.setStartAfter(afterNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    textEl.focus();

    this.hideMentionPopup();
  }

  handleMentionKeydown(e) {
    if (!this.mentionPopup.classList.contains('active')) return;

    const items = this.mentionPopup.querySelectorAll('.mention-item');
    const active = this.mentionPopup.querySelector('.mention-item.active');
    let idx = Array.from(items).indexOf(active);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      active?.classList.remove('active');
      idx = (idx + 1) % items.length;
      items[idx]?.classList.add('active');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      active?.classList.remove('active');
      idx = idx <= 0 ? items.length - 1 : idx - 1;
      items[idx]?.classList.add('active');
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (active) {
        e.preventDefault();
        active.click();
      }
    } else if (e.key === 'Escape') {
      this.hideMentionPopup();
    }
  }

  setupAIArt() {
    const btn = document.getElementById('add-aiart-btn');
    if (!btn) return;

    btn.addEventListener('click', () => this.openAIArtGenerator());
  }

  openAIArtGenerator() {
    const dialog = document.getElementById('create-pic-dialog');
    if (!dialog) return;

    if (typeof APPController !== 'undefined') {
      const self = this;
      APPController.writeNewAItoLocationFunc = function (name, imgUrl) {
        self.useGeneratedArtFromDialog(imgUrl);
      };
    }

    try { dialog.close(); } catch {}
    try { dialog.showModal(); } catch {}

    if (typeof componentHandler !== 'undefined') {
      componentHandler.upgradeDom();
    }
    if (typeof populateElfStyles === 'function') {
      populateElfStyles();
    }

    const promptInput = document.getElementById('createaiimageprompt');
    if (promptInput) promptInput.focus();
  }

  async useGeneratedArtFromDialog(imgUrl) {
    if (!imgUrl) return;
    const preview = document.getElementById('upload-preview');
    if (/^https?:\/\//i.test(imgUrl) || imgUrl.startsWith('/')) {
      const url = imgUrl.startsWith('/') ? new URL(imgUrl, window.location.origin).href : imgUrl;
      this.attachHostedMedia(url, 'image', preview);
      this.showToast('Art added to post!');
      return;
    }
    try {
      const res = await fetch(imgUrl, { cache: 'no-store' });
      const blob = await res.blob();
      const file = new File([blob], 'ai-art.jpg', { type: blob.type || 'image/jpeg' });
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      document.getElementById('media-input').files = dataTransfer.files;
      if (preview) this.showPreview(file, preview);
      this.showToast('Art added to post!');
    } catch {
      this.showToast('Failed to add art');
    }
  }

  attachHostedMedia(mediaURL, mediaType, preview) {
    if (!preview) return;
    if (this._bgUpload) {
      this._bgUpload.aborted = true;
      this._bgUpload._xhr?.abort();
    }
    const input = document.getElementById('media-input');
    if (input) input.value = '';
    this.currentMediaFile = null;
    const safeURL = this.escapeHtml(mediaURL);
    const mediaMarkup = mediaType === 'video'
      ? `<video controls playsinline src="${safeURL}" class="preview-video"></video>`
      : `<img src="${safeURL}" alt="Media preview" class="preview-image">`;
    preview.innerHTML = `<div class="preview-media-wrapper">${mediaMarkup}<div class="preview-actions"><button class="remove-preview" aria-label="Remove"><span class="close-x" aria-hidden="true">&times;</span></button></div></div>`;
    preview.classList.add('has-media');
    this._bgUpload = { done: true, url: mediaURL, mediaType, progress: 100, error: null };
    preview.querySelector('.remove-preview')?.addEventListener('click', () => {
      preview.innerHTML = '';
      preview.classList.remove('has-media');
      this._bgUpload = null;
    });
  }

  async handleRepost(e) {
    const btn = e.currentTarget;
    const postId = btn.dataset.postId;

    this.showLoading();
    try {
      const formData = new FormData();
      formData.append('id', postId);

      const res = await fetch('/api/repost', {
        method: 'POST',
        body: formData
      });

      this.hideLoading();
      if (res.ok) {
        btn.classList.add('reposted');
        this.showToast('Reposted!');
      } else {
        this.showToast('Repost failed');
      }
    } catch (err) {
      this.hideLoading();
      this.showToast('Repost failed');
    }
  }

  setupSearchAutocomplete() {
    const searchInput = document.querySelector('.search-input');
    if (!searchInput) return;

    let dropdown = document.createElement('div');
    dropdown.className = 'search-dropdown';
    searchInput.parentElement.appendChild(dropdown);

    let debounceTimer;
    let wordData = null;
    let sortedWords = null;

    const loadWords = async () => {
      if (wordData) return;
      try {
        const cdnUrl = 'https://netwrckstatic.netwrck.com/static/data/word-frequencies.json';
        const res = await fetch(cdnUrl);
        if (res.ok) {
          wordData = await res.json();
          sortedWords = Object.keys(wordData).sort((a, b) => wordData[b] - wordData[a]);
        }
      } catch {}
    };

    const getSuggestions = (query) => {
      if (!sortedWords || !query) return [];
      const words = query.toLowerCase().split(/\s+/);
      const lastWord = words[words.length - 1];
      if (lastWord.length < 2) return [];

      const used = new Set(words.slice(0, -1));
      const suggestions = [];

      for (const word of sortedWords) {
        if (word.length > 25 || used.has(word)) continue;
        if (word.startsWith(lastWord) && word !== lastWord) {
          const prefix = words.slice(0, -1).join(' ');
          suggestions.push({
            word,
            display: prefix ? `${prefix} ${word}` : word,
            highlight: word.substring(lastWord.length)
          });
          if (suggestions.length >= 6) break;
        }
      }
      return suggestions;
    };

    const showDropdown = (suggestions) => {
      if (!suggestions.length) {
        dropdown.classList.remove('active');
        return;
      }
      dropdown.innerHTML = suggestions.map((s, i) =>
        `<div class="search-suggestion${i === 0 ? ' active' : ''}" data-value="${s.display}">
          <span class="suggestion-text">${s.word.substring(0, s.word.length - s.highlight.length)}</span><span class="suggestion-highlight">${s.highlight}</span>
        </div>`
      ).join('');
      dropdown.classList.add('active');

      dropdown.querySelectorAll('.search-suggestion').forEach(el => {
        el.addEventListener('click', () => {
          searchInput.value = el.dataset.value;
          dropdown.classList.remove('active');
          searchInput.form?.submit();
        });
      });
    };

    searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const suggestions = getSuggestions(searchInput.value);
        showDropdown(suggestions);
      }, 100);
    });

    searchInput.addEventListener('keydown', (e) => {
      const items = dropdown.querySelectorAll('.search-suggestion');
      const active = dropdown.querySelector('.search-suggestion.active');
      let idx = Array.from(items).indexOf(active);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        active?.classList.remove('active');
        idx = (idx + 1) % items.length;
        items[idx]?.classList.add('active');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        active?.classList.remove('active');
        idx = idx <= 0 ? items.length - 1 : idx - 1;
        items[idx]?.classList.add('active');
      } else if ((e.key === 'Tab' || e.key === 'Enter') && active && dropdown.classList.contains('active')) {
        e.preventDefault();
        searchInput.value = active.dataset.value;
        dropdown.classList.remove('active');
        if (e.key === 'Enter') searchInput.form?.submit();
      } else if (e.key === 'Escape') {
        dropdown.classList.remove('active');
      }
    });

    searchInput.addEventListener('focus', loadWords);
    searchInput.addEventListener('blur', () => {
      setTimeout(() => dropdown.classList.remove('active'), 150);
    });

    loadWords();
  }

  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      const key = typeof e?.key === 'string' ? e.key.toLowerCase() : '';
      const openDialog = document.querySelector('dialog[open], .editor-modal.open');
      if (key === 'escape' && openDialog) {
        if (typeof openDialog.close === 'function') openDialog.close();
        else openDialog.classList.remove('open');
        return;
      }
      if (this.isShortcutInputTarget(e.target) || openDialog) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const items = this.exploreGrid?.querySelectorAll('.grid-item[data-post-id]') || [];
      const feedActive = this.feedContainer?.classList.contains('active') || this.staticPostMode;
      const activeItem = feedActive ? this.getActiveFeedItem() : null;

      switch(key) {
        case 'n':
          e.preventDefault();
          document.getElementById('create-post-btn')?.click();
          break;
        case 'l':
          e.preventDefault();
          if (!e.repeat) {
            if (feedActive) this.runFeedShortcut(activeItem, '.like-btn');
            else if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
              this.likePost(items[this.selectedIndex].dataset.postId);
            }
          }
          break;
        case 'c':
          e.preventDefault();
          if (!e.repeat) this.runFeedShortcut(activeItem, '.comment-btn');
          break;
        case 'r':
          e.preventDefault();
          if (!e.repeat) this.runFeedShortcut(activeItem, '.repost-btn');
          break;
        case 'q':
          e.preventDefault();
          if (!e.repeat) this.runFeedShortcut(activeItem, '.quote-btn');
          break;
        case 's':
          e.preventDefault();
          if (!e.repeat) this.runFeedShortcut(activeItem, '.share-btn');
          break;
        case 'j':
          e.preventDefault();
          if (feedActive) this.scrollFeedBy(1, true);
          else this.navigatePost(1, items);
          break;
        case 'k':
          e.preventDefault();
          if (feedActive) this.scrollFeedBy(-1, true);
          else this.navigatePost(-1, items);
          break;
        case 'enter':
        case 'o':
          if (!feedActive && this.selectedIndex >= 0 && items[this.selectedIndex]) {
            e.preventDefault();
            items[this.selectedIndex].click();
          }
          break;
        case '/':
          e.preventDefault();
          document.querySelector('.search-input')?.focus();
          break;
        case '?':
          e.preventDefault();
          this.toggleShortcutDialog();
          break;
        case 'escape':
          if (feedActive && !this.staticPostMode) {
            document.getElementById('feed-back-btn')?.click();
          } else {
            this.clearSelection(items);
          }
          break;
      }
    });
  }

  isShortcutInputTarget(target) {
    if (!target || typeof target.closest !== 'function') return false;
    return !!target.closest('input, textarea, select, [contenteditable="true"], button, a, [role="textbox"], [role="menu"], [role="dialog"]');
  }

  getActiveFeedItem() {
    if (this.activeFeedItem?.isConnected) return this.activeFeedItem;
    const scope = this.feedScroll || document;
    const item = scope.querySelector('.feed-item[data-post-id]');
    if (item) this.setActiveFeedItem(item);
    return item;
  }

  runFeedShortcut(item, selector) {
    if (!item) return;
    const button = item.querySelector(selector);
    if (!button || button.disabled) return;
    button.click();
  }

  toggleShortcutDialog() {
    if (!this.shortcutDialog) {
      const dialog = document.createElement('dialog');
      dialog.className = 'feed-shortcuts-dialog';
      dialog.setAttribute('aria-labelledby', 'feed-shortcuts-title');
      dialog.innerHTML = `
        <div class="feed-shortcuts-head">
          <h2 id="feed-shortcuts-title">Keyboard shortcuts</h2>
          <button type="button" class="feed-shortcuts-close" aria-label="Close"><span aria-hidden="true">&times;</span></button>
        </div>
        <div class="feed-shortcuts-grid">
          <span><kbd>J</kbd><b>Next post</b></span>
          <span><kbd>K</kbd><b>Previous post</b></span>
          <span><kbd>L</kbd><b>Like</b></span>
          <span><kbd>C</kbd><b>Comment</b></span>
          <span><kbd>R</kbd><b>Repost</b></span>
          <span><kbd>Q</kbd><b>Quote</b></span>
          <span><kbd>S</kbd><b>Share</b></span>
          <span><kbd>N</kbd><b>New post</b></span>
          <span><kbd>O</kbd><b>Open selected</b></span>
          <span><kbd>/</kbd><b>Search</b></span>
          <span><kbd>Esc</kbd><b>Back or close</b></span>
          <span><kbd>?</kbd><b>Shortcut help</b></span>
        </div>`;
      document.body.appendChild(dialog);
      dialog.querySelector('.feed-shortcuts-close')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      this.shortcutDialog = dialog;
    }
    if (this.shortcutDialog.open) this.shortcutDialog.close();
    else this.shortcutDialog.showModal();
  }

  navigatePost(direction, items) {
    if (!items.length) return;

    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      items[this.selectedIndex].classList.remove('selected');
    }

    this.selectedIndex += direction;
    if (this.selectedIndex < 0) this.selectedIndex = 0;
    if (this.selectedIndex >= items.length) this.selectedIndex = items.length - 1;

    const item = items[this.selectedIndex];
    if (item) {
      item.classList.add('selected');
      item.scrollIntoView({ block: 'center', behavior: 'auto' });
      item.focus({ preventScroll: true });
    }
  }

  clearSelection(items) {
    items?.forEach(i => i.classList.remove('selected'));
    this.selectedIndex = -1;
  }

  async likePost(postId) {
    if (!postId) return;
    try {
      const formData = new FormData();
      formData.append('id', postId);
      const res = await fetch('/api/like-post', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const item = this.exploreGrid?.querySelector(`[data-post-id="${postId}"]`);
        item?.classList.add('liked');
        this.setPostLiked(postId);
        this.showToast('Liked!');
      }
    } catch {}
  }

  setupGridInfiniteScroll() {
    const sentinel = document.getElementById('grid-sentinel');
    if (!sentinel) return;

    const scrollObserver = new window.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMore && !this.isLoadingMore) {
          this.loadMoreGridItems();
        }
      });
    }, { rootMargin: '400px' });

    scrollObserver.observe(sentinel);
  }

  setupGridVideoObserver() {
    if (this.gridVideoObserver || !('IntersectionObserver' in window)) return;

    this.gridVideoObserver = new window.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (!video || video.tagName !== 'VIDEO') return;
        if (entry.isIntersecting) {
          if (this.prefersReducedMotion) {
            video.pause();
            return;
          }
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.1 });

    document.querySelectorAll('video.grid-thumb').forEach(video => this.observeGridVideo(video));
  }

  observeGridVideo(video) {
    if (!this.gridVideoObserver || !video || video.tagName !== 'VIDEO') return;
    this.gridVideoObserver.observe(video);
  }

  async loadMoreGridItems() {
    if (this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.showLoading();

    try {
      const queryKey = this.gridQuery || '';
      let attempts = 0;
      while (attempts < 6 && this.hasMore) {
        const offset = this.gridOffsets[queryKey] || 0;
        const res = await fetch(this.buildFeedUrl(offset, this.limit, queryKey));
        if (!res.ok) throw new Error('Failed to load');
        const data = await res.json();
        const rawPosts = Array.isArray(data.posts) ? data.posts : [];
        const newPosts = rawPosts.filter(post => this.rememberGridPost(post));
        this.gridOffsets[queryKey] = offset + rawPosts.length;
        this.offset += rawPosts.length;
        this.hasMore = Boolean(data.has_more);
        if (newPosts.length) {
          this.appendGridItems(newPosts);
          break;
        }
        if (!this.hasMore || !rawPosts.length) break;
        attempts += 1;
      }
      this.hideLoading();
    } catch (err) {
      this.hideLoading();
      console.error('Load more failed:', err);
    }

    this.isLoadingMore = false;
  }

  galleryMediaKey(raw) {
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      url.search = '';
      url.hash = '';
      url.hostname = url.hostname.toLowerCase();
      return url.toString().replace(/\/$/, '');
    } catch {
      return String(raw).trim().toLowerCase().split(/[?#]/, 1)[0].replace(/\/$/, '');
    }
  }

  galleryTextKey(raw) {
    const key = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return key.length >= 40 ? key : '';
  }

  rememberGridPost(post) {
    if (!post || this.seenPostIds.has(post.id)) return false;
    const mediaKey = this.galleryMediaKey(post.media_url);
    const textKey = this.galleryTextKey(post.text);
    if ((mediaKey && this.seenMediaKeys.has(mediaKey)) || (textKey && this.seenTextKeys.has(textKey))) return false;
    this.seenPostIds.add(post.id);
    if (mediaKey) this.seenMediaKeys.add(mediaKey);
    if (textKey) this.seenTextKeys.add(textKey);
    return true;
  }

  setupFeedInfiniteScroll() {
    if (this.feedScrollObserver) {
      this.feedScrollObserver.disconnect();
    }

    let topSentinel = this.feedScroll.querySelector('.feed-top-sentinel');
    if (!topSentinel) {
      topSentinel = document.createElement('div');
      topSentinel.className = 'feed-sentinel feed-top-sentinel';
      topSentinel.innerHTML = '<div class="feed-loading-spinner feed-loading-spinner-top" style="display:none;"></div>';
      this.feedScroll.insertBefore(topSentinel, this.feedScroll.firstChild);
    }

    let sentinel = this.feedScroll.querySelector('.feed-sentinel:not(.feed-top-sentinel)');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.className = 'feed-sentinel';
      sentinel.innerHTML = '<div class="feed-loading-spinner" style="display:none;"></div>';
      this.feedScroll.appendChild(sentinel);
    }

    this.feedScrollObserver = new window.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (entry.target === topSentinel && this.feedHasPrevious && !this.isLoadingPreviousFeed) {
          this.loadPreviousFeedItems();
        } else if (entry.target === sentinel && this.feedHasMore && !this.isLoadingFeed) {
          this.loadMoreFeedItems();
        }
      });
    }, { root: this.feedScroll, rootMargin: '200px' });

    this.feedScrollObserver.observe(topSentinel);
    this.feedScrollObserver.observe(sentinel);
  }

  async loadMoreFeedItems() {
    if (this.isLoadingFeed || !this.feedHasMore) return;
    this.isLoadingFeed = true;

    const spinner = this.feedScroll.querySelector('.feed-loading-spinner');
    if (spinner) spinner.style.display = 'block';

    try {
      const res = await fetch(this.buildFeedUrl(this.feedOffset, 20, this.baseQuery));
      if (!res.ok) throw new Error('Failed to load');

      const data = await res.json();

      const posts = this.getPostsArray(data);
      if (posts.length > 0) {
        this.appendFeedItems(posts);
        this.feedOffset += posts.length;
        this.feedHasMore = data.has_more;
      } else {
        this.feedHasMore = false;
      }
    } catch (err) {
      console.error('Load more feed failed:', err);
    }

    if (spinner) spinner.style.display = 'none';
    this.isLoadingFeed = false;
  }

  async loadPreviousFeedItems() {
    if (this.isLoadingPreviousFeed || !this.feedHasPrevious) return;
    const nextOffset = Math.max(0, this.feedStartOffset - 20);
    const count = this.feedStartOffset - nextOffset;
    if (count <= 0) {
      this.feedHasPrevious = false;
      return;
    }

    this.isLoadingPreviousFeed = true;
    const spinner = this.feedScroll.querySelector('.feed-loading-spinner-top');
    if (spinner) spinner.style.display = 'block';

    try {
      const res = await fetch(this.buildFeedUrl(nextOffset, count, this.baseQuery));
      if (!res.ok) throw new Error('Failed to load');

      const data = await res.json();
      const posts = this.getPostsArray(data);
      if (posts.length > 0) {
        this.prependFeedItems(posts);
        this.feedStartOffset = nextOffset;
        this.feedHasPrevious = this.feedStartOffset > 0;
      } else {
        this.feedHasPrevious = false;
      }
    } catch (err) {
      console.error('Load previous feed failed:', err);
    }

    if (spinner) spinner.style.display = 'none';
    this.isLoadingPreviousFeed = false;
  }

  setupFeedNav() {
    this.feedNavUp = document.getElementById('feed-nav-up');
    this.feedNavDown = document.getElementById('feed-nav-down');
    if (!this.feedNavUp && !this.feedNavDown) return;

    this.feedNavUp?.addEventListener('click', () => this.scrollFeedBy(-1));
    this.feedNavDown?.addEventListener('click', () => this.scrollFeedBy(1));
  }

  async scrollFeedBy(direction, fromKeyboard = false) {
    if (!this.feedScroll) return;
    let items = Array.from(this.feedScroll.querySelectorAll('.feed-item[data-post-id]'));
    if (!items.length) return;

    let currentIndex = this.activeFeedItem ? items.indexOf(this.activeFeedItem) : -1;
    if (currentIndex < 0) {
      const viewportHeight = this.feedScroll.clientHeight || window.innerHeight;
      currentIndex = Math.round(this.feedScroll.scrollTop / viewportHeight);
    }

    let targetIndex = currentIndex + direction;
    if (targetIndex < 0 && this.feedHasPrevious) {
      await this.loadPreviousFeedItems();
      items = Array.from(this.feedScroll.querySelectorAll('.feed-item[data-post-id]'));
      currentIndex = this.activeFeedItem ? items.indexOf(this.activeFeedItem) : currentIndex;
      targetIndex = currentIndex - 1;
    } else if (targetIndex >= items.length && this.feedHasMore) {
      await this.loadMoreFeedItems();
      items = Array.from(this.feedScroll.querySelectorAll('.feed-item[data-post-id]'));
      targetIndex = currentIndex + 1;
    }

    targetIndex = Math.max(0, Math.min(items.length - 1, targetIndex));
    const target = items[targetIndex];
    target?.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    if (target) {
      this.setActiveFeedItem(target);
      if (fromKeyboard) target.focus({ preventScroll: true });
    }
  }

  setActiveFeedItem(item) {
    if (!item || !item.dataset?.postId) return;
    if (this.activeFeedItem === item) return;
    this.activeFeedItem?.classList.remove('keyboard-active');
    this.activeFeedItem = item;
    item.classList.add('keyboard-active');
    this.updateFeedUrl(item.dataset.postId);
    this.updateFeedNavState();
  }

  updateFeedUrl(postId) {
    if (!(this.updateUrlOnScroll || this.galleryFeedHistoryActive) || !postId) return;
    if (this.lastUrlPostId === postId) return;
    this.lastUrlPostId = postId;
    const url = `/p/${postId}`;
    try {
      const state = this.galleryFeedHistoryActive && !this.postFeedMode
        ? { gallery_feed: true, feed_post_id: postId, gallery_url: this.galleryUrl }
        : { feed_post_id: postId };
      window.history.replaceState(state, '', url);
    } catch {}
  }

  updateFeedNavState() {
    if (!this.feedNavUp && !this.feedNavDown) return;
    const items = Array.from(this.feedScroll?.querySelectorAll('.feed-item') || []);
    if (!items.length || !this.activeFeedItem) return;
    const idx = items.indexOf(this.activeFeedItem);
    if (this.feedNavUp) this.feedNavUp.disabled = idx <= 0 && !this.feedHasPrevious;
    if (this.feedNavDown) this.feedNavDown.disabled = idx >= items.length - 1 && !this.feedHasMore;
  }

  appendFeedItems(posts) {
    const sentinel = this.feedScroll.querySelector('.feed-sentinel:not(.feed-top-sentinel)');
    posts.forEach((post, index) => {
      const item = this.createFeedItem(post);
      if (sentinel) {
        this.feedScroll.insertBefore(item, sentinel);
      } else {
        this.feedScroll.appendChild(item);
      }
      this.observer?.observe(item);
      this.attachFeedItemListeners(item);
      if ((index + 1) % 10 === 0) {
        const ad = this.createAdItem();
        if (sentinel) {
          this.feedScroll.insertBefore(ad, sentinel);
        } else {
          this.feedScroll.appendChild(ad);
        }
      }
    });
  }

  prependFeedItems(posts) {
    const topSentinel = this.feedScroll.querySelector('.feed-top-sentinel');
    const insertBefore = topSentinel?.nextSibling || this.feedScroll.firstChild;
    const previousHeight = this.feedScroll.scrollHeight;
    const previousTop = this.feedScroll.scrollTop;

    posts.forEach(post => {
      const item = this.createFeedItem(post);
      this.feedScroll.insertBefore(item, insertBefore);
      this.observer?.observe(item);
      this.attachFeedItemListeners(item);
    });

    const heightDelta = this.feedScroll.scrollHeight - previousHeight;
    this.feedScroll.scrollTop = previousTop + heightDelta;
  }

  appendGridItems(posts) {
    if (!this.exploreGrid) return;
    posts.forEach(post => {
      const item = this.createGridItem(post);
      this.placeGridItem(item);
      this.attachGridItemListener(item);
    });
  }

  createGridItem(post) {
    const item = document.createElement('div');
    item.className = `grid-item ${post.media_type === 'video' ? 'grid-video' : ''}`;
    item.dataset.postId = post.id;
    item.dataset.type = 'post';

    const safeText = this.escapeHtml(post.text || '');
    const isVideo = post.media_type === 'video';
    if (!post.media_url) item.classList.add('grid-text-only');
    const textHTML = `<div class="grid-text-content">${post.author_avatar ? `<img class="grid-text-avatar" src="${this.escapeHtml(post.author_avatar)}" alt="" loading="lazy" decoding="async">` : ''}<p class="grid-text-body">${this.escapeHtml((post.text || '').slice(0, 120))}</p></div>`;
    const mediaHTML = !post.media_url ? textHTML : isVideo
      ? `<video class="grid-thumb" loop muted playsinline autoplay preload="metadata">
           <source src="${post.media_url}" type="video/mp4">
         </video>
         <div class="grid-video-icon"><i class="material-icons">play_arrow</i></div>`
      : `<img class="grid-thumb" src="${post.media_url}" alt="${safeText || 'Artwork'}" loading="lazy" decoding="async">`;

    const sharePath = `${isVideo ? '/p/' : '/art/'}${post.id}`;
    const shareHTML = this.isEbank
      ? `<button type="button" class="grid-share-btn art-share-btn" data-post-id="${post.id}" data-share-url="${sharePath}" data-share-media="${this.escapeHtml(post.media_url || '')}" data-share-text="${safeText}" aria-label="Share artwork"><i class="material-icons" aria-hidden="true">share</i></button>`
      : '';

    item.innerHTML = `
      <div class="grid-media">${mediaHTML}</div>
      <div class="grid-hover">
        <span class="grid-stat"><i class="material-icons">favorite</i> ${post.likes || 0}</span>
        <span class="grid-stat"><i class="material-icons">chat_bubble</i> ${post.comment_count || 0}</span>
      </div>
      ${shareHTML}`;

    return item;
  }

  imageToolButtonsHTML(mediaUrl) {
    const url = this.escapeHtml(mediaUrl);
    return `
          <button class="feed-action imgtool-btn" data-imgtool="style" data-img-url="${url}" title="Style Transfer" aria-label="Style transfer">
            <i class="material-icons">palette</i>
          </button>
          <button class="feed-action imgtool-btn" data-imgtool="removebg" data-img-url="${url}" title="Remove Background" aria-label="Remove background">
            <i class="material-icons">auto_fix_high</i>
          </button>
          <button class="feed-action imgtool-btn" data-imgtool="animate" data-img-url="${url}" title="Animate (Image to Video)" aria-label="Animate image to video">
            <i class="material-icons">movie_filter</i>
          </button>`;
  }

  handleImageTool(tool, imageUrl) {
    if (!imageUrl) return;
    if (tool === 'style') {
      this.runStyleTransfer(imageUrl);
      return;
    }
    if (!window.userData?.is_subscribed) {
      if (typeof window.showSubscribeDialog === 'function') {
        window.showSubscribeDialog();
      } else {
        window.location.href = '/signup?next=' + encodeURIComponent(window.location.pathname + window.location.search);
      }
      return;
    }
    if (tool === 'removebg') this.runRemoveBackground(imageUrl);
    else if (tool === 'animate') this.runAnimateImage(imageUrl);
  }

  getImgToolDialog() {
    let dlg = document.getElementById('imgtool-dialog');
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.id = 'imgtool-dialog';
      dlg.className = 'feed-dialog imgtool-dialog';
      dlg.innerHTML = `
        <div class="dialog-header">
          <h4 id="imgtool-title">Image Tools</h4>
          <button type="button" class="dialog-close" aria-label="Close"><span class="close-x" aria-hidden="true">&times;</span></button>
        </div>
        <div class="imgtool-body" id="imgtool-body"></div>`;
      document.body.appendChild(dlg);
      dlg.querySelector('.dialog-close').addEventListener('click', () => dlg.close());
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    }
    return dlg;
  }

  showImgTool(title, bodyHTML) {
    const dlg = this.getImgToolDialog();
    dlg.querySelector('#imgtool-title').textContent = title;
    dlg.querySelector('#imgtool-body').innerHTML = bodyHTML;
    if (!dlg.open) dlg.showModal();
  }

  imgToolLoading(title, message) {
    this.showImgTool(title, `<div class="imgtool-status"><span class="imgtool-spinner"></span>${this.escapeHtml(message)}</div>`);
  }

  imgToolError(title, message) {
    this.showImgTool(title, `<p class="imgtool-error">${this.escapeHtml(message)}</p>`);
  }

  imgToolImageResult(title, url) {
    const safe = this.escapeHtml(url);
    this.showImgTool(title, `
      <div class="imgtool-result">
        <img src="${safe}" alt="${this.escapeHtml(title)} result">
        <div class="imgtool-result-links">
          <a href="${safe}" target="_blank" rel="noopener">Open full size</a>
          <a href="${safe}" download>Download</a>
        </div>
      </div>`);
  }

  imgToolVideoResult(title, url) {
    const safe = this.escapeHtml(url);
    this.showImgTool(title, `
      <div class="imgtool-result">
        <video controls playsinline autoplay loop preload="metadata"><source src="${safe}" type="video/mp4"></video>
        <div class="imgtool-result-links">
          <a href="${safe}" target="_blank" rel="noopener">Open video</a>
          <a href="${safe}" download>Download</a>
        </div>
      </div>`);
  }

  async imgToolFetch(url, payload) {
    if (window.userData?.secret) payload.api_key = window.userData.secret;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    let data = {};
    try { data = await res.json(); } catch (err) {}
    if (!res.ok) {
      if (res.status === 401) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = (data.redirect || '/login') + '?next=' + next;
        throw new Error('Login required');
      }
      if (res.status === 402) {
        document.getElementById('imgtool-dialog')?.close();
        if (typeof window.showSubscribeDialog === 'function') window.showSubscribeDialog();
        throw new Error(data.error || 'Subscription required');
      }
      throw new Error(data.error || 'Request failed');
    }
    return data;
  }

  runStyleTransfer(imageUrl) {
    const stylePrompt = prompt('Describe the style to apply', 'anime style, vibrant colors');
    if (!stylePrompt) return;
    this.imgToolLoading('Style Transfer', 'Applying style, this can take up to a minute...');
    this.imgToolFetch('/api/style-transfer', { image_url: imageUrl, prompt: stylePrompt })
      .then((data) => {
        const url = data.image_url || data.path;
        if (url) this.imgToolImageResult('Style Transfer', url);
        else this.imgToolError('Style Transfer', 'No image returned');
      })
      .catch((err) => this.imgToolError('Style Transfer', err.message));
  }

  runRemoveBackground(imageUrl) {
    this.imgToolLoading('Remove Background', 'Removing background...');
    this.imgToolFetch('/api/remove-background', { image_url: imageUrl })
      .then((data) => {
        const url = data.result?.image?.url;
        if (url) this.imgToolImageResult('Remove Background', url);
        else this.imgToolError('Remove Background', 'No image returned');
      })
      .catch((err) => this.imgToolError('Remove Background', err.message));
  }

  runAnimateImage(imageUrl) {
    if (window.LTX23VideoDialog?.open) {
      window.LTX23VideoDialog.open({ imageUrl, prompt: 'subtle cinematic motion, the scene comes to life' });
      return;
    }
    const motion = prompt('Describe the motion', 'subtle cinematic motion, the scene comes to life');
    if (!motion) return;
    this.imgToolLoading('Animate', 'Generating video, this can take a few minutes...');
    this.imgToolFetch('/api/ltx-2.3-image-to-video', { image_url: imageUrl, prompt: motion, response_mode: 'polling' })
      .then((data) => {
        if (data.job_id) {
          this.pollImgToolJob(data.job_id);
          return;
        }
        const url = this.imgToolVideoUrl(data);
        if (url) this.imgToolVideoResult('Animate', url);
        else this.imgToolError('Animate', 'No video returned');
      })
      .catch((err) => this.imgToolError('Animate', err.message));
  }

  pollImgToolJob(jobId) {
    fetch('/api/fal/status/' + encodeURIComponent(jobId), { credentials: 'include' })
      .then((r) => r.json())
      .then((job) => {
        const status = String(job.status || '').toLowerCase();
        if (status === 'completed') {
          const url = this.imgToolVideoUrl(job);
          if (url) this.imgToolVideoResult('Animate', url);
          else this.imgToolError('Animate', 'No video returned');
          return;
        }
        if (status === 'failed') {
          this.imgToolError('Animate', job.error || 'Video generation failed');
          return;
        }
        setTimeout(() => this.pollImgToolJob(jobId), 5000);
      })
      .catch(() => setTimeout(() => this.pollImgToolJob(jobId), 8000));
  }

  imgToolVideoUrl(src) {
    if (!src || typeof src !== 'object') return '';
    if (src.video_url) return src.video_url;
    if (src.video?.url) return src.video.url;
    if (src.result) return this.imgToolVideoUrl(src.result);
    return '';
  }

  setupDialogs() {
    document.getElementById('submit-post-btn')?.addEventListener('click', () => {
      this.submitPost();
    });

    document.getElementById('submit-comment-btn')?.addEventListener('click', () => {
      this.submitComment();
    });

    const commentInput = document.getElementById('comment-input');
    commentInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.submitComment();
      }
    });
    commentInput?.addEventListener('input', () => {
      commentInput.style.height = 'auto';
      commentInput.style.height = Math.min(commentInput.scrollHeight, 100) + 'px';
    });

    document.getElementById('comments-list')?.addEventListener('click', (e) => {
      const addBtn = e.target.closest?.('.reaction-add');
      if (addBtn) {
        e.preventDefault();
        this.openReactionPicker(addBtn.dataset.commentId);
        return;
      }
      const chip = e.target.closest?.('.reaction-chip');
      if (chip) {
        e.preventDefault();
        this.toggleCommentReaction(chip.dataset.commentId, {
          emoji: chip.dataset.emoji || '',
          customEmojiId: chip.dataset.customEmojiId || 0,
        });
      }
    });

    document.getElementById('emoji-modal-dialog')?.addEventListener('close', () => {
      this.reactionCommentId = null;
    });

    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!target || typeof target.closest !== 'function') return;
      const inlineEditorLink = target.closest('a[data-inline-editor="true"]');
      if (inlineEditorLink && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        this.openInlineVideoEditor(inlineEditorLink);
        return;
      }
      const imgToolBtn = target.closest('.imgtool-btn[data-imgtool]');
      if (imgToolBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleImageTool(imgToolBtn.dataset.imgtool, imgToolBtn.dataset.imgUrl);
        return;
      }
      const quoteBtn = target.closest('.quote-btn');
      if (quoteBtn) {
        this.handleQuote(quoteBtn.dataset.postId);
      }

      const remixBtn = target.closest('.remix-btn');
      if (remixBtn && remixBtn.tagName !== 'A') {
        this.handleRemix(remixBtn.dataset.prompt, remixBtn.dataset.style);
      }
    });
    document.addEventListener('pointerover', (e) => {
      const link = e.target?.closest?.('a[data-inline-editor="true"]');
      if (link) this.preloadInlineVideoEditor(link);
    }, { passive: true });
    document.addEventListener('focusin', (e) => {
      const link = e.target?.closest?.('a[data-inline-editor="true"]');
      if (link) this.preloadInlineVideoEditor(link);
    });

    this.setupRichTextEditor();
  }

  inlineEditorURL(link) {
    const url = new URL(link.href, window.location.href);
    url.searchParams.set('source', 'gallery');
    url.searchParams.set('inline', '1');
    return url.href;
  }

  inlineEditorMedia(link) {
    try {
      return decodeURIComponent(link.dataset.editorMedia || '');
    } catch {
      return link.dataset.editorMedia || '';
    }
  }

  preloadInlineVideoEditor(link) {
    const url = this.inlineEditorURL(link);
    if (this.inlineEditorFrame?.dataset.editorUrl === url) return this.inlineEditorFrame;
    this.inlineEditorFrame?.remove();
    const host = document.getElementById('inline-editor-preload-host') || document.body.appendChild(Object.assign(document.createElement('div'), {
      id: 'inline-editor-preload-host',
      className: 'inline-editor-preload-host',
    }));
    const frame = document.createElement('iframe');
    frame.className = 'inline-video-editor-frame';
    frame.title = 'Video editor';
    frame.dataset.editorUrl = url;
    frame.addEventListener('load', () => {
      frame.dataset.ready = 'true';
      frame.classList.add('ready');
      document.querySelector('.inline-video-editor.active .inline-video-editor-preview')?.classList.add('loaded');
    });
    frame.src = url;
    host.appendChild(frame);
    this.inlineEditorFrame = frame;
    return frame;
  }

  openInlineVideoEditor(link) {
    const url = this.inlineEditorURL(link);
    const media = this.inlineEditorMedia(link);
    const type = link.dataset.editorType === 'video' ? 'video' : 'image';
    let overlay = document.getElementById('inline-video-editor');
    if (!overlay) {
      overlay = document.createElement('section');
      overlay.id = 'inline-video-editor';
      overlay.className = 'inline-video-editor';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Video editor');
      overlay.innerHTML = `
        <div class="inline-video-editor-toolbar">
          <button type="button" class="inline-video-editor-close" aria-label="Close editor">&times;</button>
          <strong>Video Editor</strong>
          <a class="inline-video-editor-full" target="_blank" rel="noopener">Open full editor</a>
        </div>
        <div class="inline-video-editor-stage">
          <div class="inline-video-editor-preview"></div>
        </div>`;
      document.body.appendChild(overlay);
      overlay.querySelector('.inline-video-editor-close').addEventListener('click', () => this.closeInlineVideoEditor());
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeInlineVideoEditor();
      });
    }
    const preview = overlay.querySelector('.inline-video-editor-preview');
    preview.classList.remove('loaded');
    preview.innerHTML = type === 'video'
      ? `<video src="${this.escapeHtml(encodeURI(media))}" muted autoplay loop playsinline></video><span class="inline-video-editor-status">Loading editor...</span>`
      : `<img src="${this.escapeHtml(encodeURI(media))}" alt=""><span class="inline-video-editor-status">Loading editor...</span>`;
    overlay.querySelector('.inline-video-editor-full').href = url;
    const frame = this.preloadInlineVideoEditor(link);
    overlay.querySelector('.inline-video-editor-stage').appendChild(frame);
    if (frame.dataset.ready === 'true') preview.classList.add('loaded');
    overlay.classList.add('active');
    document.documentElement.classList.add('inline-editor-open');
    this.inlineEditorTrigger = link;
    overlay.querySelector('.inline-video-editor-close').focus();
  }

  closeInlineVideoEditor() {
    const overlay = document.getElementById('inline-video-editor');
    overlay?.classList.remove('active');
    document.documentElement.classList.remove('inline-editor-open');
    this.inlineEditorTrigger?.focus();
  }

  setupRichTextEditor() {
    const textEl = document.getElementById('post-text');
    if (!textEl) return;

    textEl.addEventListener('keydown', (e) => {
      if (!e.ctrlKey && !e.metaKey) return;

      switch(typeof e?.key === 'string' ? e.key.toLowerCase() : '') {
        case 'b':
          e.preventDefault();
          document.execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          document.execCommand('italic');
          break;
        case 'k':
          e.preventDefault();
          const url = prompt('Enter URL:');
          if (url) document.execCommand('createLink', false, url);
          break;
        case '`':
          e.preventDefault();
          const sel = window.getSelection();
          if (sel.rangeCount) {
            const range = sel.getRangeAt(0);
            const code = document.createElement('code');
            code.appendChild(range.extractContents());
            range.insertNode(code);
          }
          break;
      }
    });

    textEl.addEventListener('paste', (e) => {
      const items = e.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type && items[i].type.startsWith('image/')) {
            e.preventDefault();
            const file = items[i].getAsFile();
            if (file) {
              const input = document.getElementById('media-input');
              const preview = document.getElementById('upload-preview');
              const dt = new DataTransfer();
              dt.items.add(file);
              if (input) input.files = dt.files;
              this.showPreview(file, preview);
            }
            return;
          }
        }
      }
      const text = e.clipboardData?.getData('text/plain') || '';
      const pastedURL = this.firstPostURL(text);
      if (pastedURL && text.trim() === pastedURL) {
        e.preventDefault();
        document.execCommand('insertText', false, text);
        this.fetchLinkPreview(pastedURL);
      }
    });

    let linkDebounce;
    textEl.addEventListener('input', () => {
      clearTimeout(linkDebounce);
      linkDebounce = setTimeout(() => {
        const text = textEl.innerText || '';
        const url = this.firstPostURL(text);
        if (url && url !== this.currentLinkPreview) {
          this.fetchLinkPreview(url);
        } else if (!url && this.currentLinkPreview) {
          const preview = document.getElementById('upload-preview');
          preview?.querySelector('.link-preview-card')?.remove();
          preview?.classList.remove('has-content');
          this.currentLinkPreview = null;
        }
      }, 500);
    });
  }

  async fetchLinkPreview(url) {
    if (this.currentLinkPreview === url) return;
    this.currentLinkPreview = url;

    const preview = document.getElementById('upload-preview');
    if (!preview) return;

    try {
      const res = await fetch(`/api/link-preview?url=${encodeURIComponent(url)}`);
      if (!res.ok) {
        this.currentLinkPreview = null;
        return;
      }

      const data = await res.json();
      if (!data.title && !data.image) {
        this.currentLinkPreview = null;
        return;
      }

      let previewImage = '';
      try {
        previewImage = data.image ? new URL(data.image, url).href : '';
      } catch {}

      preview.classList.add('has-content');
      preview.innerHTML = `
        <div class="link-preview-card">
          <button class="remove-preview" type="button" aria-label="Remove"><span class="close-x" aria-hidden="true">&times;</span></button>
          ${previewImage ? `<img src="${this.escapeHtml(previewImage)}" alt="" class="link-preview-img">` : ''}
          <div class="link-preview-text">
            <div class="link-preview-title">${this.escapeHtml(data.title || '')}</div>
            <div class="link-preview-desc">${this.escapeHtml((data.description || '').slice(0, 100))}</div>
            <div class="link-preview-url">${this.escapeHtml(data.site_name || new URL(url).hostname)}</div>
          </div>
        </div>`;

      preview.querySelector('.remove-preview')?.addEventListener('click', () => {
        preview.innerHTML = '';
        preview.classList.remove('has-content');
        this.currentLinkPreview = null;
      });
    } catch {
      this.currentLinkPreview = null;
    }
  }

  handleRemix(prompt, style) {
    document.getElementById('create-post-dialog')?.showModal();
    setTimeout(() => {
      const aiartBtn = document.getElementById('add-aiart-btn');
      aiartBtn?.click();
      setTimeout(() => {
        const promptInput = document.getElementById('createaiimageprompt');
        if (promptInput) {
          promptInput.value = prompt || '';
          promptInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, 300);
    }, 200);
  }

  async handleQuote(postId) {
    this.quotingPostId = postId;
    const feedItem = document.querySelector(`.feed-item[data-post-id="${postId}"]`);
    if (!feedItem) return;

    const post = {
      id: postId,
      text: feedItem.querySelector('.feed-caption')?.textContent || '',
      author_username: feedItem.querySelector('.author-name')?.textContent?.replace('@', '') || 'anonymous',
      author_avatar: feedItem.querySelector('.author-avatar')?.src || '',
      media_url: feedItem.querySelector('.feed-image')?.src || '',
      media_type: feedItem.querySelector('.feed-image') ? 'image' : ''
    };

    this.quotedPostData = post;
    document.getElementById('create-post-dialog')?.showModal();

    const preview = document.getElementById('upload-preview');
    if (preview) {
      preview.innerHTML = `
        <div class="quote-preview-wrapper">
          <button class="remove-quote-preview" aria-label="Remove quote"><span class="close-x" aria-hidden="true">&times;</span></button>
          ${this.createQuotedPostHTML(post)}
        </div>`;
      preview.classList.add('has-quote');

      preview.querySelector('.remove-quote-preview')?.addEventListener('click', () => {
        preview.innerHTML = '';
        preview.classList.remove('has-quote');
        this.quotedPostData = null;
      });
    }
  }

  async submitPost() {
    const textEl = document.getElementById('post-text');
    const text = textEl?.innerText?.trim() || '';
    const file = document.getElementById('media-input')?.files[0];
    const nsfwToggle = document.getElementById('post-nsfw-toggle');

    if (!text && !file && !this._bgUpload?.done) {
      this.showToast('Add some content');
      return;
    }

    const mediaType = file ? (file.type.startsWith('video/') ? 'video' : 'image') : (this._bgUpload?.mediaType || '');

    if (file && this._bgUpload && !this._bgUpload.done && !this._bgUpload.error) {
      this._draftPost = { text, mediaType, isNSFW: !!nsfwToggle?.checked };
      textEl.innerText = '';
      document.getElementById('upload-preview').innerHTML = '';
      if (nsfwToggle) nsfwToggle.checked = false;
      document.getElementById('create-post-dialog')?.close();
      this.showDraftBanner(text);
      this.showToast('Uploading in background...');
      return;
    }

    this.showLoading();
    const formData = new FormData();
    formData.append('text', text);

    if (this._bgUpload?.done && this._bgUpload.url) {
      formData.append('media_url', this._bgUpload.url);
      formData.append('media_type', mediaType);
    } else if (file) {
      formData.append('media', file);
      formData.append('media_type', mediaType);
    }
    if (nsfwToggle?.checked) {
      formData.append('is_nsfw', 'true');
    }

    try {
      const res = await fetch('/api/create-post', {
        method: 'POST',
        body: formData
      });

      this.hideLoading();
      if (res.ok) {
        textEl.innerText = '';
        document.getElementById('upload-preview').innerHTML = '';
        if (nsfwToggle) nsfwToggle.checked = false;
        document.getElementById('create-post-dialog')?.close();
        this._bgUpload = null;
        window.location.reload();
      } else {
        this.showToast(res.status === 401 ? 'Log in to post' : 'Post failed');
      }
    } catch (err) {
      this.hideLoading();
      this.showToast('Post failed');
    }
  }

  async submitComment() {
    const input = document.getElementById('comment-input');
    const text = input?.value?.trim();

    if (!text || !this.currentPostId) return;

    const list = document.getElementById('comments-list');
    const noComments = list?.querySelector('.no-comments');
    if (noComments) noComments.remove();

    const tempId = `temp-${Date.now()}`;
    const commentHTML = `
      <div class="comment-item comment-pending" id="${tempId}">
        <img class="comment-avatar" src="/static/img/android-chrome-192x192.png" alt="">
        <div class="comment-content">
          <div class="comment-author">@you<span class="comment-time">now</span></div>
          <div class="comment-text">${this.renderCommentText(text)}</div>
        </div>
      </div>`;
    list?.insertAdjacentHTML('beforeend', commentHTML);
    list?.lastElementChild?.scrollIntoView({ block: 'nearest' });

    input.value = '';
    input.style.height = 'auto';

    const formData = new FormData();
    formData.append('post_id', this.currentPostId);
    formData.append('text', text);

    try {
      const res = await fetch('/api/add-comment', {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        document.getElementById(tempId)?.classList.remove('comment-pending');
        this.bumpCommentCount(this.currentPostId);
      } else {
        document.getElementById(tempId)?.remove();
        this.showToast('Comment failed');
      }
    } catch (err) {
      document.getElementById(tempId)?.remove();
      this.showToast('Comment failed');
    }
  }

  bumpCommentCount(postId) {
    document.querySelectorAll(`.comment-btn[data-post-id="${postId}"] .comment-count`).forEach((el) => {
      const n = parseInt(el.textContent, 10);
      el.textContent = Number.isFinite(n) ? n + 1 : 1;
    });
  }

  setupUpload() {
    const dropZone = document.getElementById('create-drop-zone');
    const input = document.getElementById('media-input');
    const preview = document.getElementById('upload-preview');
    const addMediaBtn = document.getElementById('add-media-btn');

    addMediaBtn?.addEventListener('click', () => input?.click());

    dropZone?.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('dragover');
    });

    dropZone?.addEventListener('dragleave', (e) => {
      if (!dropZone.contains(e.relatedTarget)) {
        dropZone.classList.remove('dragover');
      }
    });

    dropZone?.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('dragover');
      const file = e.dataTransfer?.files[0];
      if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
        input.files = e.dataTransfer.files;
        this.showPreview(file, preview);
      }
    });

    input?.addEventListener('change', () => {
      const file = input.files[0];
      if (file) {
        this.showPreview(file, preview);
      }
    });
  }

  applyExternalComposerHandoff() {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('compose') !== '1' || !this.isAuthenticated()) return;

    const textEl = document.getElementById('post-text');
    const preview = document.getElementById('upload-preview');
    const dialog = document.getElementById('create-post-dialog');
    const text = params.get('text')?.trim() || '';
    const mediaURL = params.get('media_url')?.trim() || '';
    const mediaType = params.get('media_type') === 'video' ? 'video' : 'image';
    if (textEl && text) textEl.innerText = text;
    if (mediaURL && preview) this.attachHostedMedia(mediaURL, mediaType, preview);
    setTimeout(() => dialog?.showModal(), 60);
  }

  startBackgroundUpload(file) {
    if (this._bgUpload) {
      this._bgUpload.aborted = true;
      this._bgUpload._xhr?.abort();
    }
    const state = { file, progress: 0, done: false, url: null, aborted: false, error: null };
    this._bgUpload = state;

    const fileName = (file.name || 'upload').replace(/[,!#$%^&*(){}[\]<>;'?=":]/g, '').replace(/\s+/g, '-');
    const fileType = file.type || 'application/octet-stream';

    fetch('/file-upload-get-signed-url-cloudflare?' + new URLSearchParams({ file_type: fileType, file_name: fileName }))
      .then(r => r.json())
      .then(signedUrl => {
        if (state.aborted) return;
        const xhr = new XMLHttpRequest();
        state._xhr = xhr;
        xhr.open('PUT', signedUrl.url, true);
        xhr.setRequestHeader('Content-Type', fileType);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            state.progress = Math.round((e.loaded / e.total) * 100);
            this.updateUploadProgress(state.progress);
          }
        };
        xhr.onload = () => {
          if (state.aborted) return;
          if (xhr.status === 200) {
            const bucket = signedUrl.bucket_name || 'netwrckstatic.netwrck.com';
            state.url = `https://${bucket}/${signedUrl.object_path}`;
            state.done = true;
            state.progress = 100;
            this.updateUploadProgress(100);
            this.flushDraftIfPending();
          } else {
            state.error = 'Upload failed';
          }
        };
        xhr.onerror = () => { if (!state.aborted) state.error = 'Upload failed'; };
        xhr.send(file);
      })
      .catch(() => { if (!state.aborted) state.error = 'Upload failed'; });
  }

  updateUploadProgress(pct) {
    const bar = document.getElementById('upload-progress-bar');
    if (bar) {
      bar.style.width = pct + '%';
      if (pct >= 100) bar.classList.add('complete');
    }
  }

  flushDraftIfPending() {
    if (!this._draftPost || !this._bgUpload?.done) return;
    const draft = this._draftPost;
    this._draftPost = null;

    const formData = new FormData();
    formData.append('text', draft.text);
    formData.append('media_url', this._bgUpload.url);
    formData.append('media_type', draft.mediaType);
    if (draft.isNSFW) formData.append('is_nsfw', 'true');

    this.showToast('Posting...');
    fetch('/api/create-post', { method: 'POST', body: formData })
      .then(res => {
        if (res.ok) {
          this.showToast('Posted!');
          this.removeDraftBanner();
          this._bgUpload = null;
          setTimeout(() => window.location.reload(), 800);
        } else {
          this.showToast('Post failed');
          this.removeDraftBanner();
        }
      })
      .catch(() => {
        this.showToast('Post failed');
        this.removeDraftBanner();
      });
  }

  showDraftBanner(text) {
    this.removeDraftBanner();
    const banner = document.createElement('div');
    banner.id = 'draft-upload-banner';
    banner.style.cssText = 'position:fixed;top:60px;left:50%;transform:translateX(-50%);background:rgba(30,30,30,0.95);color:#fff;padding:10px 20px;border-radius:10px;z-index:9999;display:flex;align-items:center;gap:10px;box-shadow:0 4px 20px rgba(0,0,0,0.4);max-width:90vw;';
    const pct = this._bgUpload?.progress || 0;
    banner.innerHTML = `
      <div class="draft-spinner" style="width:18px;height:18px;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:spin 0.8s linear infinite;flex-shrink:0;"></div>
      <div style="min-width:0;">
        <div style="font-size:13px;font-weight:500;">Uploading video...</div>
        <div style="font-size:11px;color:#aaa;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${this.escapeHtml(text.slice(0, 60))}</div>
        <div style="margin-top:4px;height:3px;background:rgba(255,255,255,0.15);border-radius:2px;overflow:hidden;">
          <div id="draft-progress-bar" style="height:100%;background:#ff4081;width:${pct}%;transition:width 0.3s;border-radius:2px;"></div>
        </div>
      </div>`;
    document.body.appendChild(banner);
    if (!document.getElementById('draft-spin-style')) {
      const style = document.createElement('style');
      style.id = 'draft-spin-style';
      style.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }
    this._draftProgressInterval = setInterval(() => {
      const bar = document.getElementById('draft-progress-bar');
      const sp = banner.querySelector('.draft-spinner');
      if (bar && this._bgUpload) {
        bar.style.width = this._bgUpload.progress + '%';
        if (this._bgUpload.done && sp) sp.style.display = 'none';
      }
    }, 300);
  }

  removeDraftBanner() {
    clearInterval(this._draftProgressInterval);
    document.getElementById('draft-upload-banner')?.remove();
  }

  showPreview(file, preview) {
    if (this.currentMediaUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.currentMediaUrl);
    }
    const url = URL.createObjectURL(file);
    this.currentMediaFile = file;
    this.currentMediaUrl = url;

    this.startBackgroundUpload(file);

    if (file.type.startsWith('video/')) {
      preview.innerHTML = `
        <div class="preview-media-wrapper">
          <video src="${url}" controls class="preview-video"></video>
          <div class="upload-progress-track"><div id="upload-progress-bar" class="upload-progress-fill"></div></div>
          <div class="preview-actions">
            <button type="button" class="edit-preview-btn" aria-label="Open video editor"><i class="material-icons">movie_edit</i><span>Edit video</span></button>
            <button class="remove-preview" aria-label="Remove"><span class="close-x" aria-hidden="true">&times;</span></button>
          </div>
        </div>`;
    } else {
      preview.innerHTML = `
        <div class="preview-media-wrapper">
          <img src="${url}" alt="Preview" class="preview-image">
          <div class="upload-progress-track"><div id="upload-progress-bar" class="upload-progress-fill"></div></div>
          <div class="preview-actions">
            <button type="button" class="edit-preview-btn" aria-label="Edit image"><i class="material-icons">tune</i><span>Edit image</span></button>
            <button class="remove-preview" aria-label="Remove"><span class="close-x" aria-hidden="true">&times;</span></button>
          </div>
        </div>`;
    }

    preview.classList.add('has-media');

    preview.querySelector('.remove-preview')?.addEventListener('click', () => {
      preview.innerHTML = '';
      preview.classList.remove('has-media');
      document.getElementById('media-input').value = '';
      this.currentMediaFile = null;
      this.currentMediaUrl = null;
      if (this._bgUpload) {
        this._bgUpload.aborted = true;
        this._bgUpload._xhr?.abort();
        this._bgUpload = null;
      }
    });

    preview.querySelector('.edit-preview-btn')?.addEventListener('click', () => {
      this.openMediaEditor(file);
    });
  }

  openMediaEditor(file) {
    if (file.type.startsWith('video/')) {
      this.openFullVideoEditor(file);
      return;
    }
    this.createMediaEditorDialog();
    const dialog = document.getElementById('media-editor-dialog');
    if (!dialog) return;

    const canvas = document.getElementById('editor-canvas');
    const video = document.getElementById('editor-video');
    canvas.style.display = 'block';
    video.style.display = 'none';
    document.querySelector('.trim-controls')?.classList.remove('active');

    const img = new Image();
    img.onload = () => {
      this.editorImage = img;
      this.resetCanvas();
      this.applyCurrentFilter();
    };
    img.src = this.currentMediaUrl;

    this.currentFilter = 'normal';
    this.cropRatio = 'free';
    dialog.querySelector('.crop-ratio.active')?.classList.remove('active');
    dialog.querySelector('.crop-ratio[data-ratio="free"]')?.classList.add('active');
    this.resetPhotoAdjustments(dialog);
    this.cropActive = false;
    dialog.showModal();
  }

  async openFullVideoEditor(file) {
    const editorWindow = window.open('', '_blank');
    if (!editorWindow) {
      this.showToast('Allow popups to open the video editor');
      return;
    }
    editorWindow.document.title = 'Opening video editor';
    editorWindow.document.body.textContent = 'Preparing video editor...';
    try {
      const db = await new Promise((resolve, reject) => {
        const request = indexedDB.open('feed_editor_transfer', 1);
        request.onupgradeneeded = () => request.result.createObjectStore('files');
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      await new Promise((resolve, reject) => {
        const tx = db.transaction('files', 'readwrite');
        tx.objectStore('files').put(file, 'media');
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
      db.close();
      editorWindow.location.href = '/tools/video-editor?import=feed-upload&source=gallery';
    } catch {
      editorWindow.close();
      this.showToast('Could not open video editor');
    }
  }

  createMediaEditorDialog() {
    if (document.getElementById('media-editor-dialog')) return;

    const dialog = document.createElement('dialog');
    dialog.id = 'media-editor-dialog';
    dialog.className = 'feed-dialog media-editor-dialog';
    dialog.innerHTML = `
      <div class="dialog-header">
        <button type="button" class="dialog-close close-editor" aria-label="Close"><span class="close-x" aria-hidden="true">&times;</span></button>
        <h4>Edit</h4>
        <button type="button" class="editor-done-btn" id="editor-done-btn">Done</button>
      </div>
      <div class="editor-content">
        <div class="editor-preview">
          <canvas id="editor-canvas"></canvas>
          <video id="editor-video" controls style="display:none;"></video>
          <div class="crop-overlay" id="crop-overlay" style="display:none;"></div>
        </div>
        <div class="editor-tools">
          <div class="filter-strip">
            <button class="filter-btn active" data-filter="normal"><span class="filter-preview"></span><span>Normal</span></button>
            <button class="filter-btn" data-filter="clarendon"><span class="filter-preview"></span><span>Clarendon</span></button>
            <button class="filter-btn" data-filter="gingham"><span class="filter-preview"></span><span>Gingham</span></button>
            <button class="filter-btn" data-filter="moon"><span class="filter-preview"></span><span>Moon</span></button>
            <button class="filter-btn" data-filter="lark"><span class="filter-preview"></span><span>Lark</span></button>
            <button class="filter-btn" data-filter="reyes"><span class="filter-preview"></span><span>Reyes</span></button>
            <button class="filter-btn" data-filter="juno"><span class="filter-preview"></span><span>Juno</span></button>
            <button class="filter-btn" data-filter="slumber"><span class="filter-preview"></span><span>Slumber</span></button>
            <button class="filter-btn" data-filter="cinematic"><span class="filter-preview"></span><span>Cinematic</span></button>
            <button class="filter-btn" data-filter="ember"><span class="filter-preview"></span><span>Ember</span></button>
            <button class="filter-btn" data-filter="noir"><span class="filter-preview"></span><span>Noir</span></button>
            <button class="filter-btn" data-filter="chrome"><span class="filter-preview"></span><span>Chrome</span></button>
          </div>
          <div class="tool-tabs">
            <button class="tool-tab active" data-tool="filter"><i class="material-icons">auto_awesome</i>Filter</button>
            <button class="tool-tab" data-tool="crop"><i class="material-icons">crop</i>Crop</button>
            <button class="tool-tab" data-tool="adjust"><i class="material-icons">tune</i>Adjust</button>
          </div>
          <div class="tool-panel filter-panel active" data-panel="filter"></div>
          <div class="tool-panel crop-panel" data-panel="crop">
            <div class="crop-ratios">
              <button class="crop-ratio active" data-ratio="free">Free</button>
              <button class="crop-ratio" data-ratio="1:1">1:1</button>
              <button class="crop-ratio" data-ratio="4:3">4:3</button>
              <button class="crop-ratio" data-ratio="16:9">16:9</button>
            </div>
          </div>
          <div class="tool-panel adjust-panel" data-panel="adjust">
            <div class="adjust-grid">
              <div class="adjust-slider"><label for="brightness-slider">Exposure <output id="brightness-value">0</output></label><input type="range" id="brightness-slider" min="-100" max="100" value="0"></div>
              <div class="adjust-slider"><label for="contrast-slider">Contrast <output id="contrast-value">0</output></label><input type="range" id="contrast-slider" min="-100" max="100" value="0"></div>
              <div class="adjust-slider"><label for="highlights-slider">Highlights <output id="highlights-value">0</output></label><input type="range" id="highlights-slider" min="-100" max="100" value="0"></div>
              <div class="adjust-slider"><label for="shadows-slider">Shadows <output id="shadows-value">0</output></label><input type="range" id="shadows-slider" min="-100" max="100" value="0"></div>
              <div class="adjust-slider"><label for="saturation-slider">Saturation <output id="saturation-value">0</output></label><input type="range" id="saturation-slider" min="-100" max="100" value="0"></div>
              <div class="adjust-slider"><label for="warmth-slider">Warmth <output id="warmth-value">0</output></label><input type="range" id="warmth-slider" min="-100" max="100" value="0"></div>
              <div class="adjust-slider"><label for="tint-slider">Tint <output id="tint-value">0</output></label><input type="range" id="tint-slider" min="-100" max="100" value="0"></div>
              <div class="adjust-slider"><label for="fade-slider">Fade <output id="fade-value">0</output></label><input type="range" id="fade-slider" min="0" max="100" value="0"></div>
              <div class="adjust-slider"><label for="vignette-slider">Vignette <output id="vignette-value">0</output></label><input type="range" id="vignette-slider" min="0" max="100" value="0"></div>
            </div>
            <button type="button" class="adjust-reset" id="adjust-reset">Reset adjustments</button>
          </div>
          <div class="trim-controls">
            <input type="range" id="trim-start" min="0" max="100" value="0">
            <input type="range" id="trim-end" min="0" max="100" value="100">
            <div class="trim-labels"><span id="trim-start-time">0:00</span><span id="trim-end-time">0:00</span></div>
          </div>
        </div>
      </div>`;
    document.body.appendChild(dialog);

    dialog.querySelector('.close-editor')?.addEventListener('click', () => dialog.close());
    dialog.querySelector('#editor-done-btn')?.addEventListener('click', () => this.applyEdits());

    dialog.querySelectorAll('.filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        dialog.querySelector('.filter-btn.active')?.classList.remove('active');
        btn.classList.add('active');
        this.currentFilter = btn.dataset.filter;
        this.applyCurrentFilter();
      });
    });

    dialog.querySelectorAll('.tool-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        dialog.querySelector('.tool-tab.active')?.classList.remove('active');
        dialog.querySelector('.tool-panel.active')?.classList.remove('active');
        tab.classList.add('active');
        dialog.querySelector(`[data-panel="${tab.dataset.tool}"]`)?.classList.add('active');
      });
    });

    dialog.querySelectorAll('.crop-ratio').forEach(btn => {
      btn.addEventListener('click', () => {
        dialog.querySelector('.crop-ratio.active')?.classList.remove('active');
        btn.classList.add('active');
        this.setCropRatio(btn.dataset.ratio);
      });
    });

    ['brightness', 'contrast', 'highlights', 'shadows', 'saturation', 'warmth', 'tint', 'fade', 'vignette'].forEach(adj => {
      dialog.querySelector(`#${adj}-slider`)?.addEventListener('input', (event) => {
        const output = dialog.querySelector(`#${adj}-value`);
        if (output) output.value = event.target.value;
        this.applyCurrentFilter();
      });
    });
    dialog.querySelector('#adjust-reset')?.addEventListener('click', () => {
      this.resetPhotoAdjustments(dialog);
      this.applyCurrentFilter();
    });
  }

  resetPhotoAdjustments(dialog) {
    dialog?.querySelectorAll('.adjust-slider input[type="range"]').forEach(input => {
      input.value = '0';
      const output = dialog.querySelector(`#${input.id.replace('-slider', '-value')}`);
      if (output) output.value = '0';
    });
    dialog?.querySelector('.filter-btn.active')?.classList.remove('active');
    dialog?.querySelector('.filter-btn[data-filter="normal"]')?.classList.add('active');
    this.currentFilter = 'normal';
  }

  resetCanvas() {
    const canvas = document.getElementById('editor-canvas');
    if (!canvas || !this.editorImage) return;

    const maxW = 600, maxH = 600;
    let sourceW = this.editorImage.width;
    let sourceH = this.editorImage.height;
    let sourceX = 0;
    let sourceY = 0;
    const ratioParts = String(this.cropRatio || 'free').split(':').map(Number);
    if (ratioParts.length === 2 && ratioParts[0] > 0 && ratioParts[1] > 0) {
      const targetRatio = ratioParts[0] / ratioParts[1];
      if (sourceW / sourceH > targetRatio) {
        const croppedW = sourceH * targetRatio;
        sourceX = (sourceW - croppedW) / 2;
        sourceW = croppedW;
      } else {
        const croppedH = sourceW / targetRatio;
        sourceY = (sourceH - croppedH) / 2;
        sourceH = croppedH;
      }
    }
    let w = sourceW, h = sourceH;
    if (w > maxW) { h = h * maxW / w; w = maxW; }
    if (h > maxH) { w = w * maxH / h; h = maxH; }

    canvas.width = Math.max(1, Math.round(w));
    canvas.height = Math.max(1, Math.round(h));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(this.editorImage, sourceX, sourceY, sourceW, sourceH, 0, 0, canvas.width, canvas.height);
  }

  applyCurrentFilter() {
    const canvas = document.getElementById('editor-canvas');
    if (!canvas || !this.editorImage) return;
    const ctx = canvas.getContext('2d');

    this.resetCanvas();

    const adjustment = id => (Number(document.getElementById(`${id}-slider`)?.value) || 0) / 100;
    const preset = this.getFilterValues(this.currentFilter);
    const brightness = adjustment('brightness');
    const contrast = 1 + adjustment('contrast') + (preset.contrast || 0);
    const highlights = adjustment('highlights');
    const shadows = adjustment('shadows');
    const saturation = 1 + adjustment('saturation') + (preset.saturation || 0);
    const warmth = adjustment('warmth') + (preset.warmth || 0);
    const tint = adjustment('tint') + (preset.tint || 0);
    const fade = Math.max(0, adjustment('fade') + (preset.fade || 0));
    const vignette = Math.max(0, adjustment('vignette') + (preset.vignette || 0));

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      let r = data[i], g = data[i + 1], b = data[i + 2];

      r = r + brightness * 255;
      g = g + brightness * 255;
      b = b + brightness * 255;

      r = ((r / 255 - 0.5) * contrast + 0.5) * 255;
      g = ((g / 255 - 0.5) * contrast + 0.5) * 255;
      b = ((b / 255 - 0.5) * contrast + 0.5) * 255;

      let gray = 0.2989 * r + 0.587 * g + 0.114 * b;
      const shadowMask = Math.pow(1 - Math.max(0, Math.min(1, gray / 255)), 2);
      const highlightMask = Math.pow(Math.max(0, Math.min(1, gray / 255)), 2);
      const toneShift = shadows * shadowMask * 150 + highlights * highlightMask * 150;
      r += toneShift;
      g += toneShift;
      b += toneShift;

      r += warmth * 46 + tint * 24;
      g += warmth * 8 - tint * 32;
      b -= warmth * 46 - tint * 24;

      gray = 0.2989 * r + 0.587 * g + 0.114 * b;
      r = gray + saturation * (r - gray);
      g = gray + saturation * (g - gray);
      b = gray + saturation * (b - gray);

      r = r * preset.r;
      g = g * preset.g;
      b = b * preset.b;

      if (fade > 0) {
        const fadeMix = Math.min(0.45, fade * 0.35);
        r = r * (1 - fadeMix) + 36 * fadeMix;
        g = g * (1 - fadeMix) + 38 * fadeMix;
        b = b * (1 - fadeMix) + 44 * fadeMix;
      }

      if (vignette > 0) {
        const x = (i / 4) % canvas.width;
        const y = Math.floor(i / 4 / canvas.width);
        const dx = (x / Math.max(1, canvas.width - 1) - 0.5) * 2;
        const dy = (y / Math.max(1, canvas.height - 1) - 0.5) * 2;
        const edge = Math.max(0, Math.sqrt(dx * dx + dy * dy) - 0.25);
        const shade = Math.max(0.25, 1 - Math.min(0.75, edge * vignette * 0.72));
        r *= shade;
        g *= shade;
        b *= shade;
      }

      data[i] = Math.max(0, Math.min(255, r));
      data[i + 1] = Math.max(0, Math.min(255, g));
      data[i + 2] = Math.max(0, Math.min(255, b));
    }

    ctx.putImageData(imageData, 0, 0);
  }

  getFilterValues(filter) {
    const filters = {
      normal: { r: 1, g: 1, b: 1 },
      clarendon: { r: 1.1, g: 1.05, b: 1.15 },
      gingham: { r: 0.95, g: 1.02, b: 1.08 },
      moon: { r: 0.85, g: 0.85, b: 0.95 },
      lark: { r: 1.08, g: 1.08, b: 1.0 },
      reyes: { r: 1.0, g: 1.02, b: 0.95 },
      juno: { r: 1.1, g: 1.0, b: 0.9 },
      slumber: { r: 0.9, g: 0.88, b: 1.05, fade: 0.12 },
      cinematic: { r: 1.02, g: 0.98, b: 1.08, contrast: 0.16, saturation: -0.08, warmth: -0.08, vignette: 0.28 },
      ember: { r: 1.12, g: 1.01, b: 0.9, contrast: 0.12, saturation: 0.14, warmth: 0.18, vignette: 0.12 },
      noir: { r: 1, g: 1, b: 1, contrast: 0.26, saturation: -1, fade: 0.04, vignette: 0.32 },
      chrome: { r: 0.96, g: 1.04, b: 1.12, contrast: 0.18, saturation: 0.08, warmth: -0.14 }
    };
    return filters[filter] || filters.normal;
  }

  setCropRatio(ratio) {
    this.cropRatio = ratio;
    this.applyCurrentFilter();
  }

  applyEdits() {
    const canvas = document.getElementById('editor-canvas');
    const video = document.getElementById('editor-video');
    const dialog = document.getElementById('media-editor-dialog');

    if (canvas.style.display !== 'none' && canvas.width > 0) {
      canvas.toBlob((blob) => {
        const file = new File([blob], 'edited-image.jpg', { type: 'image/jpeg' });
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        document.getElementById('media-input').files = dataTransfer.files;
        const preview = document.getElementById('upload-preview');
        dialog?.close();
        if (preview) this.showPreview(file, preview);
      }, 'image/jpeg', 0.9);
    } else {
      dialog?.close();
    }
  }

  showToast(msg) {
    const toast = document.createElement('div');
    toast.style.cssText = 'position:fixed;bottom:100px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.8);color:#fff;padding:12px 24px;border-radius:8px;z-index:9999;';
    toast.textContent = msg;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  renderCommentText(raw) {
    return formatCommentText(raw);
  }

  relativeTime(value) {
    return fmtRelativeTime(value);
  }

  async syncAuthState() {
    if (window.authSession && typeof window.authSession.checkStatus === 'function') {
      try {
        await window.authSession.checkStatus();
      } catch {}
    }

    if (window.authSession && window.authSession.isLoggedIn && window.authSession.isLoggedIn()) {
      const currentUser = window.authSession.getCurrentUser && window.authSession.getCurrentUser();
      if (currentUser && currentUser.id) {
        window.userData = window.userData || {};
        if (!window.userData.id) window.userData.id = currentUser.id;
        if (!window.userData.email && currentUser.email) window.userData.email = currentUser.email;
        if (!window.userData.username && currentUser.username) window.userData.username = currentUser.username;
        if (!window.userData.name && currentUser.name) window.userData.name = currentUser.name;
        if (!window.userData.secret && (currentUser.secret || currentUser.api_key)) {
          window.userData.secret = currentUser.secret || currentUser.api_key;
        }
        if (currentUser.is_subscribed !== undefined) {
          window.userData.is_subscribed = currentUser.is_subscribed;
        }
      }
    }

    if (!this.getUserSecret()) {
      try {
        const authRes = await fetch('/api/current-user', { credentials: 'include' });
        if (authRes.ok) {
          const authJson = await authRes.json();
          const user = authJson?.user || authJson?.user_data || authJson?.data || authJson;
          if (authJson?.logged_in && user && (user.id || user.ID)) {
            window.userData = {
              ...(window.userData || {}),
              id: user.id || user.ID,
              email: user.email || user.Email,
              username: user.username || user.Username || user.name || user.Name,
              name: user.name || user.Name || user.username || user.Username,
              secret: user.secret || user.api_key || user.apiKey || user.Secret,
              is_subscribed: user.is_subscribed !== undefined ? user.is_subscribed : (window.userData || {}).is_subscribed,
              free_credits: user.free_credits || user.FreeCredits || (window.userData || {}).free_credits || 0,
              paid_credits: user.paid_credits || user.PaidCredits || (window.userData || {}).paid_credits || 0,
              credits: user.credits || user.Credits || ((user.free_credits || 0) + (user.paid_credits || 0)) || (window.userData || {}).credits || 0
            };
            try {
              if (window.userData.secret) localStorage.setItem('userSecret', window.userData.secret);
              localStorage.setItem('userData', JSON.stringify(window.userData));
            } catch {}
          }
        }
      } catch {}
    }
  }

  getSubscriptionStatus() {
    if (window.authSession && typeof window.authSession.isSubscribed === 'function') {
      const status = window.authSession.isSubscribed();
      if (typeof status === 'boolean') return status;
    }
    if (window.userData && typeof window.userData.is_subscribed === 'boolean') {
      return window.userData.is_subscribed;
    }
    if (typeof window.isUserSubscribed === 'function') {
      const status = window.isUserSubscribed();
      if (typeof status === 'boolean') return status;
    }
    return null;
  }

  isAuthenticated() {
    if (window.AuthUtils && window.AuthUtils.isUserAuthenticated) {
      const auth = window.AuthUtils.isUserAuthenticated();
      if (auth && auth.isAuthenticated) return true;
    }
    if (window.authSession && window.authSession.isLoggedIn && window.authSession.isLoggedIn()) return true;
    if (window.userData && window.userData.id) return true;
    if (window.userData && window.userData.secret && window.userData.secret !== 'anonymous') return true;
    return document.cookie.includes('session=') ||
           document.cookie.includes('user_secret=') ||
           document.body.dataset.authenticated === 'true' ||
           !!document.querySelector('[data-user-id]');
  }

  getUserSecret() {
    return (window.userData && (window.userData.secret || window.userData.api_key)) ||
      window.userSecret ||
      (window.authSession && window.authSession.getUserSecret && window.authSession.getUserSecret()) ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('userSecret') : '') ||
      '';
  }

  setupSwipeGesture(item) {
    let startX = 0;
    let startY = 0;
    let currentX = 0;

    item.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      currentX = startX;
    }, { passive: true });

    item.addEventListener('touchmove', (e) => {
      currentX = e.touches[0].clientX;
      const diffX = currentX - startX;
      const diffY = Math.abs(e.touches[0].clientY - startY);

      if (Math.abs(diffX) > diffY && Math.abs(diffX) > 20) {
        item.style.transform = `translateX(${diffX * 0.3}px)`;
        item.style.opacity = 1 - Math.abs(diffX) / 600;
      }
    }, { passive: true });

    item.addEventListener('touchend', () => {
      const diffX = currentX - startX;
      item.style.transform = '';
      item.style.opacity = '';

      if (diffX > 100) {
        this.showRecommendations(item.dataset.postId);
      } else if (diffX < -100) {
        this.skipPost(item);
      }
    });
  }

  createRecommendationsOverlay() {
    if (document.getElementById('recommendations-overlay')) return;

    const overlay = document.createElement('div');
    overlay.id = 'recommendations-overlay';
    overlay.className = 'recommendations-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'recommendations-title');
    overlay.innerHTML = `
      <div class="recommendations-panel">
        <div class="recommendations-header">
          <h3 id="recommendations-title">Similar Posts</h3>
          <button class="close-recommendations" aria-label="Close"><span class="close-x" aria-hidden="true">&times;</span></button>
        </div>
        <div class="recommendations-list" id="recommendations-list" aria-live="polite"></div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.querySelector('.close-recommendations').addEventListener('click', () => {
      overlay.classList.remove('active');
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.classList.remove('active');
    });
    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        overlay.classList.remove('active');
      }
    });
  }

  async showRecommendations(postId) {
    const overlay = document.getElementById('recommendations-overlay');
    const list = document.getElementById('recommendations-list');
    if (!overlay || !list) return;

    list.innerHTML = '<p class="loading-text">Finding similar posts...</p>';
    overlay.classList.add('active');
    overlay.querySelector('.close-recommendations')?.focus();
    this.showLoading();

    try {
      const res = await fetch(`/api/recommend-posts?post_id=${postId}&limit=5`);
      if (!res.ok) throw new Error(`recommendations ${res.status}`);
      const posts = await res.json();
      if (!Array.isArray(posts)) throw new Error('invalid recommendations');
      if (posts.length === 0) {
        list.innerHTML = '<p class="empty-text">No similar posts found</p>';
      } else {
        list.innerHTML = posts.map(p => `
            <a href="/p/${p.id}" class="recommendation-item" data-post-id="${p.id}">
              <div class="rec-media">
                ${p.media_type === 'video'
                  ? `<video src="${this.escapeHtml(encodeURI(p.media_url || ''))}" muted loop playsinline></video>`
                  : `<img src="${this.escapeHtml(encodeURI(p.media_url || ''))}" alt="">`}
              </div>
              <div class="rec-info">
                <span class="rec-author">@${this.escapeHtml(p.author_username || 'anonymous')}</span>
                <p class="rec-text">${this.escapeHtml(p.text || '').substring(0, 60)}...</p>
                <span class="rec-likes"><i class="material-icons">favorite</i> ${Number(p.likes) || 0}</span>
              </div>
            </a>
          `).join('');

        list.querySelectorAll('.recommendation-item').forEach(item => {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            overlay.classList.remove('active');
            this.openFullscreenView(item.dataset.postId);
          });
        });
      }
    } catch {
      list.innerHTML = '<p class="empty-text">Failed to load recommendations</p>';
    } finally {
      this.hideLoading();
    }
  }

  skipPost(item) {
    item.style.transition = 'transform 0.3s, opacity 0.3s';
    item.style.transform = 'translateX(-100%)';
    item.style.opacity = '0';
    setTimeout(() => {
      item.style.transition = '';
      item.style.transform = '';
      item.style.opacity = '';
      const nextItem = item.nextElementSibling;
      if (nextItem?.classList.contains('feed-item')) {
        nextItem.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.feedController = new FeedController();
});

export { FeedController };
